import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import AccessTime from "@mui/icons-material/AccessTime";

import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  Dialog,
  FormControl,
  Grid2,
  IconButton,
  InputLabel,
  ListItemIcon,
  MenuItem,
  Menu,
  Checkbox,
  TextField,
  Typography,
  useTheme,
  Button,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Select,
  Tab,
  Tooltip,
  Link,
  Switch,
  FormGroup,
  FormControlLabel,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import ViewListIcon from "@mui/icons-material/ViewList";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import SettingsIcon from "@mui/icons-material/Settings";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  useCreateTagMutation,
  useDeleteBugMutation,
  useGetBugDetailByBugIdQuery,
  useGetConfigDetailsQuery,
  useGetListOfStatusQuery,
  useGetListOfTagsQuery,
  useGetProjectByIdQuery,
  useGetProjectForDropDownQuery,
  useGetProjectStatusLabelsMutation,
  useUpdateBugDetailMutation,
  useGetSprintListQuery,
  useUpdateBugIndexMutation,
  useGetListOfBugsQuery,
  useCreateLogMutation,
  useLazyGetListOfLogsQuery,
  useUpdateWorkLogMutation,
  useGetListOfMembersQuery,
} from "@/features/bughurd/api";
import { useNavigate } from "react-router-dom";
import AnnotationEditor from "./AnnotationEditor";
import AttachmentViewModal from "./AttachmentViewModal";
import CustomerPanel from "./CustomerPanel";
import { useGetAttachmentUrlMutation } from "@/features/Global/api";
import { LoadingButton, TabContext, TabList, TabPanel } from "@mui/lab";
import { toast } from "react-toastify";
import storageService from "@/Components/helpers/storageService";
import { useGetAiCompanyConfigQuery } from "@/features/AiApi/chatApi";
import AiNotConfiguredDialog from "@/Components/Common/AiNotConfiguredDialog";
import axios from "axios";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SearchIcon from "@mui/icons-material/Search";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import LinkIcon from "@mui/icons-material/Link";
import FlagIcon from "@mui/icons-material/Flag";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import PeopleIcon from "@mui/icons-material/People";
import LabelIcon from "@mui/icons-material/Label";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FolderIcon from "@mui/icons-material/Folder";
import CloudIcon from "@mui/icons-material/Cloud";
import LanguageIcon from "@mui/icons-material/Language";
import SchoolIcon from "@mui/icons-material/School";
import CategoryIcon from "@mui/icons-material/Category";
import TimerIcon from "@mui/icons-material/Timer";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import RepeatIcon from "@mui/icons-material/Repeat";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import UpdateIcon from "@mui/icons-material/Update";
import PersonIcon from "@mui/icons-material/Person";
import DevicesIcon from "@mui/icons-material/Devices";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import HighestIcon from "@/assets/icons/highest.svg?react";
import HighIcon from "@/assets/icons/high.svg?react";
import MediumIcon from "@/assets/icons/medium.svg?react";
import LowIcon from "@/assets/icons/low.svg?react";
import { hexToRgba } from "@/Components/helpers/util";
import Comments from "./Comments";
import { StyledChip, StyledSelect, StyledTextField } from "./styled";
import DeleteConfirmModal from "@/Components/Common/DeleteConfirmModal";
import RichTextEditor from "@/Components/Common/RichTextEditor";
import BugJourneyTimelineDialog from "./BugJourney";
import AttachmentListView from "./AttachmentListView";
import AttachmentGridView from "./AttachmentGridView";
import moment from "moment";
import { useAppSelector } from "@/app/hooks";
import DeviceDetailsModal from "./DeviceDetailsModal";
import RecurringTaskPopover, {
  RecurringConfig,
} from "@/pages/Board/components/RecurringTaskPopover";
import Relationship from "../components/Relationship";
import { getActiveSprints, getDraftSprints, typeToIconMapping } from "../utils";
import LogsTab from "../BugHurd/logs";
import VideoPreviewPanel from "./VideoPreviewPanel";
import SubtaskTab from "../components/SubtaskTab";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import UISessionTab from "../components/ui-session/UiSession";
import LinkedTask from "../components/LinkedTask";
import { SummaryVoiceRecorder } from "@/Components/VoiceRecorder";
import {
  processVideoTranscription,
  isVideoFile,
} from "@/utils/videoTranscription";
import BugAnalysis from "./BugAnalysis";

interface TaskUpdateDrawerProps {
  open: boolean;
  onClose: () => void;
  setBugId: (id: string) => void;
  setDrawerOpen: (open: boolean) => void;
  bugId: string;
  users: any[];
  createdBy: string;
  setCreatedBy: (name: string) => void;
  refreshColumn?: () => void;
  onTaskUpdate?: (
    taskId: string,
    updatedTask: any | null,
    newColumnId?: string,
  ) => void;
  task?: any; // Made optional
  bugFor?: string; // Optional bugFor to include in update payload
  onSuccess?: () => void; // Optional callback after successful update
  isCurrentUserClient?: boolean;
  reactFlow?: any;
}

function TaskUpdateDrawer({
  open,
  onClose,
  setBugId,
  setDrawerOpen,
  bugId,
  users,
  createdBy,
  setCreatedBy,
  refreshColumn,
  onTaskUpdate,
  bugFor,
  onSuccess,
  isCurrentUserClient,
  reactFlow,
}: TaskUpdateDrawerProps) {
  const { t } = useTranslation(["bugherd"]);
  const SUMMARY_MAX = 250;
  const DESCRIPTION_MAX = 2000;

  const { data: aiConfigData } = useGetAiCompanyConfigQuery({});
  const isAiConfigured = useMemo(() => {
    const providers = aiConfigData?.data?.apiProviders || [];
    return providers?.some((p: any) => p?.isActive && p?.apiKey?.trim());
  }, [aiConfigData]);

  const [showAiConfigDialog, setShowAiConfigDialog] = useState(false);

  const baseUrl = window?.location?.origin;
  const isSniffer =
    baseUrl?.includes("sniffer") || baseUrl?.includes("pmohub") || false;
  const isPmoHub = baseUrl?.includes("pmohub") || false;

  const navigate = useNavigate();
  const theme = useTheme();
  const { roleCodeDetails: myPermissions } =
    useAppSelector((state) => state.appGlobal) || {};
  const { projects: projectlist = [], columns = {} } =
    useAppSelector((state) => state.board) || {};

  const [selectedEpic, setSelectedEpic] = useState("");
  const [view] = useState(true);
  const [attachmentView, setAttachmentView] = useState("grid");
  const [isAnnotationEditorOpen, setIsAnnotationEditorOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [isAttachmentViewOpen, setIsAttachmentViewOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [imageUrls, setImageUrls] = useState([]);
  const [isJiraConfigured, setIsJiraConfigured] = useState(false);
  const [_statusList, setStatusList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  const [pendingUploads, setPendingUploads] = useState(new Set());
  const [isDeviceDetailsModalOpen, setIsDeviceDetailsModalOpen] =
    useState(false);
  const [timeLogs, setTimeLogs] = useState<{
    [key: string]: {
      hours: number;
      date: string;
      _id?: string;
      timeSpendInSeconds?: number;
    };
  }>({});
  const [selectedAssigneeForLog, setSelectedAssigneeForLog] = useState("");
  const [assigneeMenuAnchor, setAssigneeMenuAnchor] = useState(null);

  const [isSavingTimeLog, setIsSavingTimeLog] = useState(false);
  const [parsedDeviceDetails, setParsedDeviceDetails] = useState(null);

  // Voice recording state
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [isVoiceFinalizing, setIsVoiceFinalizing] = useState(false);
  const [summaryProcessingMessage, setSummaryProcessingMessage] = useState("");
  const [descProcessingMessage, setDescProcessingMessage] = useState("");

  // Video transcription state
  const [transcribingVideos, setTranscribingVideos] = useState<Set<string>>(
    new Set(),
  );
  const [transcriptionProgress, setTranscriptionProgress] = useState<
    Map<string, string>
  >(new Map());
  const [videoFiles, setVideoFiles] = useState<Map<string, File>>(new Map());

  const [bugDetail, setBugDetail] = useState<any>({
    _id: "",
    platform: "",
    project: "",
    summary: "",
    description: "",
    severity: "",
    assignTo: [],
    pageUrl: "",
    updatedAt: "",
    bugStatus: "",
    screenShots: [],
    attachments: [],
    tags: [],
    projectKey: "",
    createdAt: new Date(),
    type: "",
    jiraDatas: {},
    parentBug: null,
    startDate: "",
    dueDate: "",
    estimation: "",
    estimationDays: "",
    estimationHours: "",
    estimationMinutes: "",
    bugJourney: [],
    bugKey: "",
    deviceDetails: "",
    isRecurring: false,
    recurringData: null,
    boardType: "",
    currentSprint: null,
    isBackLog: false,
    logs: {
      console: [],
      network: [],
      actions: [],
    },
    requiredSkillSet: "",
  });

  const [recurringAnchorEl, setRecurringAnchorEl] =
    useState<HTMLElement | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringConfig, setRecurringConfig] = useState<RecurringConfig>({
    isRecurring: false,
    frequency: "Daily",
    trigger: "When done",
    skipWeekends: false,
    createNewTask: true,
    updateStatusTo: "",
    repeatTimes: 5,
    customInterval: 1,
    customUnit: "week",
    selectedDays: [],
    selectedMonthlyDays: [],
    monthlyOption: "same_day",
    startDate: new Date(),
    endOnDate: new Date(),
    isEndOn: false,
    selectedTime: "09:00",
  });
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);
  const [isAnnotatingAttachment, setIsAnnotatingAttachment] = useState(false);
  const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(null);
  const [screenshotMap, setScreenshotMap] = useState({});
  const [videoUrl, setVideoUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [isDeleteReply, setIsDeleteReply] = useState(false);
  // Video preview state
  const [isVideoPreviewOpen, setIsVideoPreviewOpen] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
  const [combinedAttachments, setCombinedAttachments] = useState([]);
  const [combiningAttachments, setCombiningAttachments] = useState(true);
  const [validationErrors, setValidationErrors] = useState<{
    bugStatus?: string;
  }>({});
  const [dependencies, setDependencies] = useState<any[]>([]);
  const [dependenciesAnchorEl, setDependenciesAnchorEl] =
    useState<HTMLElement | null>(null);

  // Story link state
  const [selectedStory, setSelectedStory] = useState<string | null>(null);
  const [storyError, setStoryError] = useState("");

  //api calls
  const { data: projects, isLoading: isLoadingProjects } =
    useGetProjectForDropDownQuery({});

  const [getProjectStatusLabels] = useGetProjectStatusLabelsMutation();
  const { data: configDataList, isLoading: isLoadingConfig } =
    useGetConfigDetailsQuery({});
  const configData = configDataList?.data?.decryptedData;
  const { data: bugData, isLoading } = useGetBugDetailByBugIdQuery(
    { bugId, withDetails: true },
    {
      refetchOnMountOrArgChange: true,
      skip: !bugId, // Added skip condition for when bugId is not provided
    },
  );
  const bugDetailData = bugData?.data?.decryptedData;

  const [getWorkLogs, { data: workLogList }] = useLazyGetListOfLogsQuery();
  useEffect(() => {
    if (!bugDetailData || !bugDetailData?.assignTo) {
      return;
    }

    if (
      Array.isArray(bugDetailData.assignTo) &&
      bugDetailData.assignTo.length >= 1
    ) {
      const firstAssigneeId = extractId(bugDetailData.assignTo[0]);
      setSelectedAssigneeForLog(firstAssigneeId);
    }
  }, [bugDetailData?.assignTo, bugDetailData]);
  useEffect(() => {
    if (bugDetailData) {
      getWorkLogs({
        bugId: bugDetailData?.parentBug || bugDetailData?._id,
        projectId: bugDetailData?.project?._id || bugDetailData?.project,
        subTaskId: bugDetailData?.parentBug ? bugDetailData?._id : undefined,
        ...(selectedAssigneeForLog && { userId: selectedAssigneeForLog }),
      });
    }
  }, [bugDetailData, selectedAssigneeForLog, bugId]);
  const getOtherWeeksTotal = () => {
    const currentWeekDates = getCurrentWeekDates().map(formatDateKey);

    let totalHours = 0;
    Object.entries(timeLogs).forEach(([dateKey, log]) => {
      // If this date is not in current week, add to total
      if (!currentWeekDates.includes(dateKey)) {
        totalHours += log.hours;
      }
    });

    return totalHours;
  };
  useEffect(() => {
    if (workLogList?.data?.decryptedData?.items) {
      const logsMap = {};
      // Sort logs by updatedAt in descending order (newest first)
      const sortedLogs = [...workLogList?.data?.decryptedData?.items].sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      // Process each worklog item from backend
      sortedLogs.forEach((log) => {
        try {
          // Validate workDate exists
          if (!log?.workDate) {
            console.warn("Skipping log with missing workDate:", log);
            return;
          }

          // Extract date in YYYY-MM-DD format from workDate
          const workDate = new Date(log.workDate);

          // Check if date is valid
          if (isNaN(workDate.getTime())) {
            console.warn("Invalid workDate:", log.workDate);
            return;
          }

          const dateKey = workDate.toISOString().split("T")[0];

          // Parse hours from timeSpend (e.g., "4h" -> 4)
          const hours = parseFloat(log.timeSpend?.replace("h", "") || "0");

          // Only add if this date doesn't already exist (since we sorted by newest first)
          if (!logsMap[dateKey]) {
            logsMap[dateKey] = {
              hours: hours,
              date: dateKey,
              _id: log._id,
              timeSpendInSeconds: log.timeSpendInSeconds,
            };
          }
        } catch (error) {
          console.error("Error processing worklog:", log, error);
        }
      });

      setTimeLogs(logsMap);
    } else {
      setTimeLogs({});
    }
  }, [workLogList, selectedAssigneeForLog, bugId]);

  const [logTime] = useCreateLogMutation();

  const [updateBug, { isLoading: updatingBug }] = useUpdateBugDetailMutation();
  const [postAttachmentKey, { isLoading: isLoadingAttachment }] =
    useGetAttachmentUrlMutation();
  const [updateWorkLog, { isLoading: isUpdatingWorkLog }] =
    useUpdateWorkLogMutation();
  // Add these helper functions
  const handleTimeLogChange = async (date: string, hours: number) => {
    const existingLog = timeLogs[date];

    // Optimistically update UI
    setTimeLogs((prev) => ({
      ...prev,
      [date]: {
        hours,
        date,
        _id: existingLog?._id,
      },
    }));

    setIsSavingTimeLog(true);
    try {
      const skillSet = bugDetailData?.requiredSkillSet || "";
      if (hours === 0 && existingLog?._id) {
        // Remove from local state when hours is 0
        setTimeLogs((prev) => {
          const newLogs = { ...prev };
          delete newLogs[date];
          return newLogs;
        });

        // You can add delete API call here if you have one
        // await deleteWorkLog({ id: existingLog._id }).unwrap();

        toast.success(t("Time log removed"));
      } else if (existingLog?._id) {
        // ✅ UPDATE existing worklog
        const response = await updateWorkLog({
          id: existingLog._id,
          bug: bugDetail._id || bugDetail?.id,
          timeSpend: `${hours}h`,
          skillSet: Array.isArray(skillSet) ? skillSet[0] : skillSet || "",
          project: bugDetailData?.project?._id,
          type: bugDetail?.type || "BUG",
          workDate: new Date(date).toISOString(),
          ...(selectedAssigneeForLog && { tempUserId: selectedAssigneeForLog }),
        }).unwrap();

        // Refetch to get updated data
        if (response?.data?.decryptedData) {
          setTimeLogs((prev) => ({
            ...prev,
            [date]: {
              hours,
              date,
              id: response?.data?.decryptedData?._id,
              timeSpendInSeconds:
                response.data.decryptedData?.timeSpendInSeconds,
            },
          }));
        }

        toast.success(t("Time log updated successfully"));
      } else {
        // ✅ CREATE new worklog
        const response = await logTime({
          bug: bugDetailData?.parentBug || bugDetailData?._id,
          timeSpend: `${hours}h`,
          skillSet: Array.isArray(skillSet) ? skillSet[0] : skillSet,
          project: bugDetailData?.project?._id || bugDetailData?.project,
          type: bugDetail?.type || "BUG",
          workDate: new Date(date).toISOString(),
          subtask: bugDetailData?.parentBug ? bugDetailData?.id : undefined,
          ...(selectedAssigneeForLog && { tempUserId: selectedAssigneeForLog }),
        }).unwrap();
        // Refetch to get new data with _id
        if (response?.data?.decryptedData?._id) {
          setTimeLogs((prev) => ({
            ...prev,
            [date]: {
              hours,
              date,
              id: response?.data?.decryptedData?._id,
              timeSpendInSeconds:
                response.data.decryptedData?.timeSpendInSeconds,
            },
          }));
        }

        toast.success(t("Time logged successfully"));
      }

      // Refetch to ensure consistency - wait for it to complete
      if (bugDetailData) {
        await getWorkLogs({
          bugId: bugDetailData?.parentBug || bugDetailData?._id,
          projectId: bugDetailData?.project?._id || bugDetailData?.project,
          subTaskId: bugDetailData?.parentBug ? bugDetailData?._id : undefined,
          ...(selectedAssigneeForLog && { userId: selectedAssigneeForLog }),
        });
      }
    } catch (error) {
      console.error("Error logging time:", error);
      toast.error(t("Failed to log time"));

      // Revert optimistic update on error
      if (existingLog) {
        setTimeLogs((prev) => ({
          ...prev,
          [date]: existingLog,
        }));
      } else {
        setTimeLogs((prev) => {
          const newLogs = { ...prev };
          delete newLogs[date];
          return newLogs;
        });
      }
    } finally {
      setIsSavingTimeLog(false);
    }
  };

  const getCurrentWeekDates = () => {
    const today = new Date();
    const currentDay = today.getDay();
    const diff = currentDay === 0 ? -6 : 1 - currentDay;

    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      weekDates.push(date);
    }

    return weekDates;
  };

  const formatDateKey = (date: Date) => {
    return date.toISOString().split("T")[0];
  };

  // Extract project ID from bugDetail for status query
  const getProjectId = () => {
    if (typeof bugDetail?.project === "object" && bugDetail.project?._id) {
      return bugDetail.project._id;
    }
    if (typeof bugDetail?.project === "string") {
      return bugDetail.project;
    }
    if (typeof bugDetailData?.project === "object" && bugDetailData?._id) {
      return bugDetailData?.project._id;
    }
    if (typeof bugDetailData?.project === "string") {
      return bugDetailData?.project;
    }
    return null;
  };

  const [updateBugIndex] = useUpdateBugIndexMutation();

  const { data: statusLists, isLoading: isLoadingStatusLabel } =
    useGetListOfStatusQuery(
      { projectId: getProjectId() },
      {
        skip: !getProjectId(),
      },
    );

  const availableStatus = statusLists?.data?.decryptedData?.items || [];

  // Extract project ID for tags query
  const getProjectIdForTags = () => {
    return bugDetail?.project?._id || bugDetail?.project;
  };

  const { data: tagList, refetch: refetchTags } = useGetListOfTagsQuery(
    getProjectIdForTags(),
    {
      skip: !getProjectIdForTags(),
    },
  );
  const availableTags = tagList?.data?.decryptedData?.items || [];

  const [createTag] = useCreateTagMutation();

  // Extract project ID for project details query
  const getProjectIdForDetails = () => {
    if (
      typeof bugDetailData?.project === "object" &&
      bugDetailData?.project?._id
    ) {
      return bugDetailData?.project._id;
    }
    if (typeof bugDetailData?.project === "string") {
      return bugDetailData?.project;
    }
    return null;
  };

  const { data: companyMemberData } = useGetListOfMembersQuery({
    projectId: getProjectIdForDetails(),
    page: 1,
    size: 100,
    searchName: "",
  });

  const { data: projectDetails } = useGetProjectByIdQuery(
    getProjectIdForDetails(),
    {
      skip: !getProjectIdForDetails(),
    },
  );
  const isSupport = projectDetails?.data?.decryptedData?.configurationType === "SUPPORT" || false;

  // Fetch stories for story link dropdown
  const {
    data: storyData,
    refetch: refetchStories,
    isFetching: isStoriesFetching,
  } = useGetListOfBugsQuery(
    {
      id: getProjectIdForDetails(),
      type: "STORY",
      page: 1,
      limit: 100,
    },
    {
      skip:
        !getProjectIdForDetails() ||
        (bugDetail?.type !== "BUG" &&
          bugDetail?.type !== "DEFECT" &&
          bugDetail?.type !== "SUGGESTIONS"),
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: sprintListData, isLoading: isSprintLoading } =
    useGetSprintListQuery(
      {
        projectId: getProjectIdForDetails() || "",
        page: 1,
        limit: 100,
        statuses: ["DRAFT", "ACTIVE"],
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !getProjectIdForDetails(),
      },
    );
  const sprints = sprintListData?.data?.decryptedData?.items || [];
  const draftSprints = getDraftSprints(sprints);
  const activeSprints = getActiveSprints(sprints);

  const jiraprojectList = projectDetails?.data?.decryptedData?.jiraConfig;
  const availableUsers = useMemo(() => {
    if (!bugDetail?.project) {
      return [];
    }
    const memberItems = companyMemberData?.data?.decryptedData?.items || [];
    return memberItems
      .filter(
        (item) =>
          item?.userRole === "TEAM_MEMBER" || item?.userRole === "TEAM_LEAD",
      )
      .map((item) => ({
        _id: item?.user?._id || item?._id,
        fullName:
          `${item?.user?.firstName || ""} ${item?.user?.lastName || ""}`.trim(),
        email: item?.user?.email,
        userRole: item?.userRole,
      }));
  }, [bugDetail?.project, companyMemberData]);

  const severityOptions = [
    { value: "Highest", label: t("Highest"), icon: <HighestIcon /> },
    { value: "High", label: t("High"), icon: <HighIcon /> },
    { value: "Medium", label: t("Medium"), icon: <MediumIcon /> },
    { value: "Low", label: t("Low"), icon: <LowIcon /> },
  ];

  const getIssueTypeIcon = (type: string) => {
    const typeInfo = typeToIconMapping[type as keyof typeof typeToIconMapping];
    if (!typeInfo) return null;
    return (
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "3px",
          backgroundColor: typeInfo.bgcolor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {typeInfo.icon}
      </Box>
    );
  };

  const issueType = [
    { value: "STORY", label: t("Story"), icon: getIssueTypeIcon("STORY") },
    { value: "BUG", label: t("Defect"), icon: getIssueTypeIcon("BUG") },
    ...(bugDetail?.parentBug === null
      ? [{ value: "EPIC", label: t("Epic"), icon: getIssueTypeIcon("EPIC") }]
      : []),
    {
      value: "SUGGESTIONS",
      label: t("Suggestions"),
      icon: getIssueTypeIcon("SUGGESTIONS"),
    },
  ];

  const skillSetOptions = [
    "Frontend Development",
    "Backend Development",
    "Full Stack Development",
    "DevOps",
    "QA/Testing",
    "UI/UX Design",
    "Mobile Development",
    "Data Science",
    "Machine Learning",
    "Security",
  ];
  const issueTypeItems = projectDetails?.data?.decryptedData?.itemTypes?.length
    ? projectDetails?.data?.decryptedData?.itemTypes
    : issueType;
  const timeLogOptions = [0, 0.5, 1, 1.5, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  // Story link helpers
  const getSeverityIcon = (severity: string) => {
    const option = severityOptions.find(
      (opt) => opt.value.toLowerCase() === severity?.toLowerCase(),
    );
    return option?.icon || null;
  };

  const stories = useMemo(() => {
    if (!storyData?.data?.decryptedData?.items) return [];
    return storyData.data.decryptedData.items.map((story: any) => ({
      id: story._id,
      summary: story.summary,
      severity: story.severity,
      status: story.bugStatus,
    }));
  }, [storyData]);
  // Generate minutes options (0, 15, 30, 45)
  const minuteOptions = [
    { value: "0", label: "0" },
    { value: "15", label: "15" },
    { value: "30", label: "30" },
    { value: "45", label: "45" },
  ];

  // Function to parse device details
  const parseDeviceDetails = (deviceDetails: string) => {
    if (!deviceDetails) return null;

    try {
      const parsed = JSON.parse(deviceDetails);
      return parsed;
    } catch (error) {
      return null;
    }
  };

  const getDeviceName = (deviceDetails: string) => {
    if (!deviceDetails) return null;

    const parsed = parseDeviceDetails(deviceDetails);
    if (
      parsed &&
      typeof parsed === "object" &&
      Object.keys(parsed)?.length > 0
    ) {
      const parts = [];

      // Handle operatingSystem - can be string or object
      if (parsed.operatingSystem) {
        if (typeof parsed.operatingSystem === "string") {
          parts.push(parsed.operatingSystem);
        } else if (typeof parsed.operatingSystem === "object") {
          const osName = parsed.operatingSystem.name || "";
          const osVersion = parsed.operatingSystem.version || "";
          parts.push(osVersion ? `${osName} ${osVersion}` : osName);
        }
      }

      // Handle browser - can be string or object
      if (parsed.browser) {
        if (typeof parsed.browser === "string") {
          parts.push(parsed.browser);
        } else if (typeof parsed.browser === "object") {
          const browserName = parsed.browser.name || "";
          const browserVersion = parsed.browser.version || "";
          parts.push(
            browserVersion ? `${browserName} ${browserVersion}` : browserName,
          );
        }
      }

      return parts.join(" - ") || "Device Info";
    } else {
      return deviceDetails;
    }
  };

  const handleDeviceDetailsClick = () => {
    const parsed = parseDeviceDetails(bugDetail.deviceDetails);
    if (parsed) {
      setParsedDeviceDetails(parsed);
      setIsDeviceDetailsModalOpen(true);
    }
  };

  // Compute systemInfo for Info tab
  const systemInfo = useMemo(() => {
    const parsed = parseDeviceDetails(bugDetail.deviceDetails);

    // Try to get screenshot dimensions from attachments
    let screenshotDims = "";
    if (bugDetail.attachments && bugDetail.attachments.length > 0) {
      const screenshot = bugDetail.attachments.find(
        (att: any) =>
          att.fileName?.includes("screenshot") ||
          att.fileType?.startsWith("image/"),
      );
      if (screenshot && screenshot.width && screenshot.height) {
        screenshotDims = `${screenshot.width}x${screenshot.height}`;
      }
    }

    // Extract OS info - handle both string and object format
    let osInfo = "";
    if (parsed?.operatingSystem) {
      if (typeof parsed.operatingSystem === "string") {
        osInfo = parsed.operatingSystem;
      } else if (typeof parsed.operatingSystem === "object") {
        const osName = parsed.operatingSystem.name || "";
        const osVersion = parsed.operatingSystem.version || "";
        osInfo = osVersion ? `${osName} ${osVersion}` : osName;
      }
    }

    // Extract Browser info - handle both string and object format
    let browserInfo = "";
    if (parsed?.browser) {
      if (typeof parsed.browser === "string") {
        browserInfo = parsed.browser;
      } else if (typeof parsed.browser === "object") {
        const browserName = parsed.browser.name || "";
        const browserVersion = parsed.browser.version || "";
        browserInfo = browserVersion
          ? `${browserName} ${browserVersion}`
          : browserName;
      }
    }

    // Extract Screen/Window Size - handle both string and object format
    let windowSizeInfo = "";
    if (parsed?.screen) {
      if (typeof parsed.screen === "string") {
        windowSizeInfo = parsed.screen;
      } else if (typeof parsed.screen === "object") {
        windowSizeInfo =
          parsed.screen.windowSize || parsed.screen.resolution || "";
      }
    }

    return {
      url: bugDetail.pageUrl || "",
      timestamp: bugDetail.createdAt,
      os: osInfo,
      browser: browserInfo,
      windowSize: windowSizeInfo,
      screenshotDimensions: screenshotDims,
    };
  }, [
    bugDetail.deviceDetails,
    bugDetail.pageUrl,
    bugDetail.createdAt,
    bugDetail.attachments,
  ]);

  const updateEstimation = (days: string, hours: string, minutes: string) => {
    const daysNum = parseInt(days || "0");
    const hoursNum = parseInt(hours || "0");
    const minutesNum = parseInt(minutes || "0");

    const parts = [];
    if (daysNum > 0) {
      parts.push(`${daysNum}d`);
    }
    if (hoursNum > 0) {
      parts.push(`${hoursNum}h`);
    }
    if (minutesNum > 0) {
      parts.push(`${minutesNum}m`);
    }

    return parts.join(" ");
  };

  // Function to parse estimation string to days, hours and minutes
  const parseEstimation = (estimationStr: string) => {
    let days = "";
    let hours = "";
    let minutes = "";

    if (estimationStr) {
      const dayMatch = estimationStr.match(/(\d+)d/);
      const hourMatch = estimationStr.match(/(\d+)h/);
      const minuteMatch = estimationStr.match(/(\d+)m/);

      if (dayMatch) days = dayMatch[1];
      if (hourMatch) hours = hourMatch[1];
      if (minuteMatch) minutes = minuteMatch[1];
    }

    return { days, hours, minutes };
  };

  // Function to convert frontend recurring config to backend format
  const convertToBackendRecurringData = (config: RecurringConfig) => {
    const baseData = {
      completionStatus:
        config.trigger === "When done" ? "WHEN_DONE" : "ON_SCHEDULE",
      isCreate: config.createNewTask || false,
      isUpdate: !!config.updateStatusTo,
      updationStatus: config.updateStatusTo || null,
      isRepeat: config.repeatTimes > 0,
      repeatMargin: config.repeatTimes || 0,
      isEndOn: config.isEndOn || false,
      endOn:
        config.isEndOn && config.endOnDate
          ? moment(config.endOnDate).format("YYYY-MM-DD HH:mm")
          : null,
    };

    // Handle different frequencies
    if (config.frequency === "Daily") {
      return {
        executionInfo: {
          executionFrequencyUnit: "DAYS",
          executionFrequencyInterval: 1,
          executionTime: config.selectedTime || "09:00",
          skipWeekends: config.skipWeekends || false,
        },
        ...baseData,
      };
    } else if (config.frequency === "Weekly") {
      const weekdayMap = {
        Su: "SUNDAY",
        Mo: "MONDAY",
        Tu: "TUESDAY",
        We: "WEDNESDAY",
        Th: "THURSDAY",
        Fr: "FRIDAY",
        Sa: "SATURDAY",
      };

      const selectedDays = (config.selectedDays || [])
        .map((day) => weekdayMap[day])
        .filter(Boolean);

      return {
        executionInfo: {
          executionFrequencyUnit: "WEEKS",
          executionDayInWeek:
            selectedDays?.length === 1 ? selectedDays[0] : selectedDays,
          executionTime: config.selectedTime || "09:00",
          executionFrequencyInterval: 1,
        },
        ...baseData,
      };
    } else if (config.frequency === "Monthly") {
      const monthOptionMap = {
        same_day: "SAME_DAY",
        third_tuesday: "THIRD_TUESDAY",
        first_day: "FIRST_DAY",
        last_day: "LAST_DAY",
      };

      const executionInfo: any = {
        executionFrequencyUnit: "MONTHS",
        monthOption: monthOptionMap[config.monthlyOption] || "SAME_DAY",
        executionTime: config.selectedTime || "09:00",
        executionFrequencyInterval: 1,
      };

      if (config.monthlyOption === "same_day" && config.startDate) {
        executionInfo.customStartDate = moment(config.startDate).format(
          "YYYY-MM-DD HH:mm",
        );
      }

      return {
        executionInfo,
        ...baseData,
      };
    }

    // Default fallback
    return {
      executionInfo: {
        executionFrequencyUnit: "DAYS",
        executionFrequencyInterval: 1,
        executionTime: config.selectedTime || "09:00",
        skipWeekends: config.skipWeekends || false,
      },
      ...baseData,
    };
  };

  // Function to convert backend recurring data to frontend format
  const convertFromBackendRecurringData = (recurringData: any): any => {
    if (!recurringData) {
      return {
        isRecurring: false,
        frequency: "Daily",
        trigger: "When done",
        skipWeekends: false,
        createNewTask: true,
        updateStatusTo: "",
        repeatTimes: 5,
        customInterval: 1,
        customUnit: "week",
        selectedDays: [],
        selectedMonthlyDays: [],
        monthlyOption: "same_day",
        startDate: new Date(),
        endOnDate: new Date(),
        isEndOn: false,
        selectedTime: "09:00",
      };
    }

    const baseConfig = {
      isRecurring: true,
      trigger:
        recurringData.completionStatus === "WHEN_DONE"
          ? "When done"
          : "On schedule",
      createNewTask: recurringData.isCreate || false,
      updateStatusTo: recurringData.updationStatus || "",
      repeatTimes: recurringData.repeatMargin || 5,
      isEndOn: recurringData.isEndOn || false,
      endOnDate: recurringData.endOn
        ? moment(recurringData.endOn).toDate()
        : new Date(),
      selectedTime: recurringData.executionInfo?.executionTime || "09:00",
      startDate: recurringData.executionInfo?.customStartDate
        ? moment(recurringData.executionInfo.customStartDate).toDate()
        : new Date(),
    };

    // Handle different frequency units
    if (recurringData.executionInfo?.executionFrequencyUnit === "DAYS") {
      return {
        ...baseConfig,
        frequency: "Daily",
        skipWeekends: recurringData.executionInfo.skipWeekends || false,
        selectedDays: [],
        selectedMonthlyDays: [],
        monthlyOption: "same_day",
        customInterval: 1,
        customUnit: "day",
      };
    } else if (
      recurringData.executionInfo?.executionFrequencyUnit === "WEEKS"
    ) {
      const weekdayMap = {
        SUNDAY: "Su",
        MONDAY: "Mo",
        TUESDAY: "Tu",
        WEDNESDAY: "We",
        THURSDAY: "Th",
        FRIDAY: "Fr",
        SATURDAY: "Sa",
      };

      const executionDays = recurringData.executionInfo.executionDayInWeek;
      const selectedDays = Array.isArray(executionDays)
        ? executionDays.map((day) => weekdayMap[day]).filter(Boolean)
        : executionDays
          ? [weekdayMap[executionDays]].filter(Boolean)
          : [];

      return {
        ...baseConfig,
        frequency: "Weekly",
        skipWeekends: false,
        selectedDays,
        selectedMonthlyDays: [],
        monthlyOption: "same_day",
        customInterval: 1,
        customUnit: "week",
      };
    } else if (
      recurringData.executionInfo?.executionFrequencyUnit === "MONTHS"
    ) {
      const monthOptionMap = {
        SAME_DAY: "same_day",
        THIRD_TUESDAY: "third_tuesday",
        FIRST_DAY: "first_day",
        LAST_DAY: "last_day",
      };

      return {
        ...baseConfig,
        frequency: "Monthly",
        skipWeekends: false,
        selectedDays: [],
        selectedMonthlyDays: [],
        monthlyOption:
          monthOptionMap[recurringData.executionInfo.monthOption] || "same_day",
        customInterval: 1,
        customUnit: "month",
      };
    }

    return {
      ...baseConfig,
      frequency: "Daily",
      skipWeekends: false,
      selectedDays: [],
      selectedMonthlyDays: [],
      monthlyOption: "same_day",
      customInterval: 1,
      customUnit: "day",
    };
  };
  const { data: epicsData } = useGetListOfBugsQuery(
    {
      id: getProjectIdForDetails(),
      // type: "EPIC",
      showEpic: true,
      viewType: "TIMELINE",
      page: 1,
      limit: 100,
    },
    {
      skip: !getProjectIdForDetails(),
    },
  );

  const epics = useMemo(() => {
    return epicsData?.data?.decryptedData?.items || [];
  }, [epicsData]);

  const handleRecurringToggle = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = event.target.checked;
    setIsRecurring(checked);

    if (checked) {
      setRecurringAnchorEl(
        event.currentTarget.closest(".MuiFormControlLabel-root") as HTMLElement,
      );
      // Set default values for recurring config when enabling
      const defaultConfig = {
        ...recurringConfig,
        isRecurring: true,
        updateStatusTo: availableStatus[0]?._id || "",
      };
      setRecurringConfig(defaultConfig);
      setIsRecurringModalOpen(true);
    } else {
      // Disable recurring
      setRecurringConfig((prev) => ({
        ...prev,
        isRecurring: false,
      }));
      setBugDetail((prev) => ({
        ...prev,
        isRecurring: false,
        recurringData: null,
      }));
    }
  };
  useEffect(() => {
    if (bugDetailData?.requiredSkillSet) {
      setBugDetail((prev) => ({
        ...prev,
        requiredSkillSet: Array.isArray(bugDetailData?.requiredSkillSet)
          ? bugDetailData?.requiredSkillSet[0] || ""
          : bugDetailData?.requiredSkillSet || "",
      }));
    }
  }, [bugDetailData]);
  useEffect(() => {
    if (bugDetailData?.epic) {
      const epicId = extractId(bugDetailData?.epic);
      setSelectedEpic(epicId);
    } else {
      setSelectedEpic("");
    }
  }, [bugDetailData?.epic]);

  const handleRecurringConfigClick = (event: React.MouseEvent<HTMLElement>) => {
    setRecurringAnchorEl(
      event.currentTarget.closest(".MuiFormControlLabel-root") as HTMLElement,
    );
    setIsRecurringModalOpen(true);
  };

  const handleRecurringConfigSave = (config: RecurringConfig) => {
    // Ensure the config has isRecurring set to true
    const configWithRecurring = {
      ...config,
      isRecurring: true,
    };

    const backendRecurringData =
      convertToBackendRecurringData(configWithRecurring);

    setRecurringConfig(configWithRecurring);
    setIsRecurring(true);

    setBugDetail((prev) => ({
      ...prev,
      isRecurring: true,
      recurringData: backendRecurringData,
    }));

    setIsRecurringModalOpen(false);
    setRecurringAnchorEl(null);
  };

  const handleRecurringModalClose = () => {
    setIsRecurringModalOpen(false);
    setRecurringAnchorEl(null);
    if (!recurringConfig.isRecurring) {
      setIsRecurring(false);
    }
  };

  // Add this helper function after other helper functions
  const parseLinkBugs = (linkBugs: any[]) => {
    const dependencies = [];

    linkBugs?.forEach((linkBug) => {
      const task = linkBug.bug;
      const dependencyTask = {
        ...task,
        depType: linkBug.type, // Use depType instead of type
        status:
          linkBug.type === "Waiting"
            ? "Waiting On"
            : linkBug.type === "Blocked"
              ? "Blocking"
              : "Linked",
        projectId: task?.project?._id || task?.project,
        projectKey: task?.project?.key || "",
      };
      dependencies.push(dependencyTask);
    });

    return dependencies;
  };

  const formatDependenciesForAPI = (dependencies: any[]) => {
    const linkBugs = dependencies.map((task) => ({
      type:
        task.status === "Waiting On"
          ? "Waiting"
          : task.status === "Blocking"
            ? "Blocked"
            : "Linked",
      bug: task._id,
    }));

    return linkBugs;
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowForMenu(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowForMenu(null);
  };

  const handleChange = (fieldKey, value, type) => {
    setBugDetail((prev) => {
      return {
        ...prev,
        jiraDatas: {
          ...prev.jiraDatas,
          [fieldKey]: type === "number" ? Number(value) : value,
        },
      };
    });
  };

  const handleProjectSelect = async (e) => {
    const selectedProjectId = e.target.value;
    const selectedProject = projectlist?.find(
      (project) => project._id === selectedProjectId,
    );

    setBugDetail((prev) => ({
      ...prev,
      project: selectedProjectId,
      projectKey: selectedProject?.key || "",
      bugStatus: "",
    }));
  };

  const handleJiraProjectSelect = async (e) => {
    const selectedProject = projects?.find(
      (project) => project.id === e.target.value,
    );

    setBugDetail((prev) => ({
      ...prev,
      project: e.target.value,
      projectKey: selectedProject?.key || "",
    }));
  };

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEstimationDaysChange = (value: string) => {
    const estimation = updateEstimation(
      value,
      bugDetail.estimationHours || "",
      bugDetail.estimationMinutes || "",
    );
    setBugDetail((prev) => ({
      ...prev,
      estimationDays: value,
      estimation: estimation,
    }));
  };

  const handleEstimationHoursChange = (value: string) => {
    const estimation = updateEstimation(
      bugDetail.estimationDays || "",
      value,
      bugDetail.estimationMinutes || "",
    );
    setBugDetail((prev) => ({
      ...prev,
      estimationHours: value,
      estimation: estimation,
    }));
  };

  const handleEstimationMinutesChange = (value: string) => {
    const estimation = updateEstimation(
      bugDetail.estimationDays || "",
      bugDetail.estimationHours || "",
      value,
    );
    setBugDetail((prev) => ({
      ...prev,
      estimationMinutes: value,
      estimation: estimation,
    }));
  };

  // Story link handler
  const handleStorySelect = (storyId: string | null) => {
    setSelectedStory(storyId);
    setStoryError("");
  };

  const hasPendingUploads = () => {
    return (
      pendingUploads.size > 0 ||
      bugDetail.attachments.some((att) => att.isUploading) ||
      transcribingVideos.size > 0
    );
  };
  const saveDisabledReasons = [
    bugDetailData?.platform === "JIRA" && !getProjectIdForDetails()
      ? t("Select a project to save")
      : null,
    !bugDetail?.summary ? t("Summary is required") : null,
    hasPendingUploads() ? t("Uploads in progress") : null,
    !DEFEKT_TRACKER.UPDATE(myPermissions)
      ? t("You don't have permission to update")
      : null,
  ].filter(Boolean);

  const handleSystemPaste = async (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    const files = [];
    for (let i = 0; i < items?.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files?.length > 0) {
      event.preventDefault();
      await processFiles(files);
      toast.success(`${files?.length} ${t("file(s) pasted from clipboard")}`);
    }
  };

  const processFiles = async (files) => {
    setIsUploading(true);

    const uploadPromises = files.map(async (file) => {
      const fileId = `${file.name}_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      setPendingUploads((prev) => new Set(prev).add(fileId));

      // Store video file reference for later transcription
      if (isVideoFile(file)) {
        setVideoFiles((prev) => new Map(prev).set(fileId, file));
      }

      try {
        if (!file.type.startsWith("image/")) {
          const formData = new FormData();
          formData.append("file", file, file.name);

          const response = await axios.post(
            `${import.meta.env.VITE_COMPANY_API_URL}/file/upload`,
            formData,
            {
              headers: {
                "Content- Type": "multipart/form-data",
              },
              withCredentials: true,
            },
          );

          const uploadUrl = response?.data?.data?.decryptedData?.Key;
          if (!uploadUrl) {
            throw new Error(`Failed to upload file: ${file.name}`);
          }

          const newAttachment = {
            fileType: file.type,
            fileName: file.name,
            url: uploadUrl,
            serverUrl: uploadUrl,
            isUploading: false,
            uploadFailed: false,
            fileId: fileId, // Add fileId for transcription
          };

          setBugDetail((prev) => ({
            ...prev,
            attachments: [...prev.attachments, newAttachment],
          }));
        } else {
          const base64Data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target?.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          const tempAttachment = {
            fileType: file.type,
            fileName: file.name,
            url: base64Data,
            isUploading: true,
            uploadFailed: false,
            fileId: fileId, // Add fileId for transcription
          };

          setBugDetail((prev) => ({
            ...prev,
            attachments: [...prev.attachments, tempAttachment],
          }));

          const uploadUrl = await uploadScreenshot(
            base64Data,
            undefined,
            file.name,
          );
          if (!uploadUrl) {
            throw new Error(`Failed to upload image: ${file.name}`);
          }

          setBugDetail((prev) => ({
            ...prev,
            attachments: prev.attachments.map((att) => {
              if (att.fileName === file.name && att.url === base64Data) {
                return {
                  ...att,
                  serverUrl: uploadUrl,
                  isUploading: false,
                  uploadFailed: false,
                };
              }
              return att;
            }),
          }));
        }
      } catch (error) {
        console.error(`Error uploading file "${file.name}":`, error);

        setBugDetail((prev) => ({
          ...prev,
          attachments: prev.attachments.map((att) => {
            if (att.fileName === file.name) {
              return {
                ...att,
                isUploading: false,
                uploadFailed: true,
              };
            }
            return att;
          }),
        }));

        toast.error(`Failed to upload "${file.name}"`);
      } finally {
        setPendingUploads((prev) => {
          const newSet = new Set(prev);
          newSet.delete(fileId);
          return newSet;
        });
      }
    });

    try {
      await Promise.allSettled(uploadPromises);
    } catch (error) {
      console.error("Error in batch upload:", error);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle video transcription when button is clicked
  const handleTranscribeVideo = async (fileId: string, fileName: string) => {
    let videoFile = videoFiles.get(fileId);
    let videoUrl = null;

    // If video file not in memory, we need to get the S3 URL for direct streaming
    if (!videoFile) {
      // Find the attachment in bugDetail.attachments or videoRecordings
      const attachment =
        bugDetail.attachments.find(
          (att) =>
            att.fileId === fileId ||
            att.fileName === fileName ||
            att.name === fileName ||
            att._id === fileId,
        ) ||
        bugDetailData?.videoRecordings?.find(
          (rec) =>
            rec.fileName === fileName ||
            rec.fileName === fileId ||
            rec._id === fileId,
        );

      if (!attachment) {
        setTranscriptionProgress((prev) =>
          new Map(prev).set(fileId, "Attachment not found ✗"),
        );
        setTimeout(() => {
          setTranscriptionProgress((prev) => {
            const newMap = new Map(prev);
            newMap.delete(fileId);
            return newMap;
          });
        }, 3000);
        return;
      }

      // Check all possible URL fields
      const possibleUrl =
        attachment.serverUrl || attachment.url || attachment.fileUrl;

      if (!possibleUrl) {
        setTranscriptionProgress((prev) =>
          new Map(prev).set(fileId, "Video URL not found ✗"),
        );
        setTimeout(() => {
          setTranscriptionProgress((prev) => {
            const newMap = new Map(prev);
            newMap.delete(fileId);
            return newMap;
          });
        }, 3000);
        return;
      }

      try {
        // Get the actual video URL (decrypt if needed)
        videoUrl = possibleUrl;

        // If URL starts with "dev/" (S3 path), decrypt it using the API
        if (videoUrl.startsWith("dev/") || !videoUrl.startsWith("http")) {
          setTranscriptionProgress((prev) =>
            new Map(prev).set(fileId, "Preparing video"),
          );

          // Use serverUrl for the key if available, otherwise use url
          const keyToFetch = attachment.serverUrl || attachment.url;

          try {
            const urlResponse = await postAttachmentKey({
              key: keyToFetch,
            }).unwrap();

            // Extract the decrypted URL from the response (matching other usages in the file)
            videoUrl =
              urlResponse?.data?.decryptedData?.url ||
              urlResponse?.data?.decryptedData ||
              urlResponse?.decryptedData?.url ||
              urlResponse?.decryptedData ||
              urlResponse?.url ||
              videoUrl;

            // Verify we got a valid HTTP URL
            if (!videoUrl.startsWith("http")) {
              throw new Error("Decryption did not return a valid HTTP URL");
            }
          } catch (decryptError) {
            console.error("❌ Decryption error:", decryptError);
            throw new Error("Failed to decrypt video URL");
          }
        }
      } catch (error) {
        console.error("❌ Failed to get video URL:", error);
        setTranscriptionProgress((prev) =>
          new Map(prev).set(fileId, "Failed to get video URL ✗"),
        );
        setTimeout(() => {
          setTranscriptionProgress((prev) => {
            const newMap = new Map(prev);
            newMap.delete(fileId);
            return newMap;
          });
        }, 5000);
        return;
      }
    }

    // Check if already transcribing
    if (transcribingVideos.has(fileId)) {
      return;
    }

    // Ensure we have either a File object or a URL
    if (!videoFile && !videoUrl) {
      setTranscriptionProgress((prev) =>
        new Map(prev).set(fileId, "Video not found ✗"),
      );
      setTimeout(() => {
        setTranscriptionProgress((prev) => {
          const newMap = new Map(prev);
          newMap.delete(fileId);
          return newMap;
        });
      }, 3000);
      return;
    }

    // Add to transcribing videos set
    setTranscribingVideos((prev) => new Set(prev).add(fileId));
    setTranscriptionProgress((prev) =>
      new Map(prev).set(fileId, "Preparing video"),
    );

    try {
      // Process video transcription with progress updates
      // Use videoFile if available (newly uploaded), otherwise use videoUrl (from S3)
      const videoSource = videoFile || videoUrl;
      const transcriptionText = await processVideoTranscription(
        videoSource,
        "en",
        // accessToken,
        (progressMessage) => {
          setTranscriptionProgress((prev) =>
            new Map(prev).set(fileId, progressMessage),
          );
        },
      );

      // Append transcription to description with proper formatting
      setBugDetail((prev) => {
        const hasExistingText = prev.description.trim().length > 0;
        const spacing = hasExistingText ? "\n\n" : "";
        const formattedTranscription = `${spacing}Video Summary: \n${transcriptionText}`;

        return {
          ...prev,
          description: prev.description + formattedTranscription,
        };
      });

      // Update progress to complete
      setTranscriptionProgress((prev) =>
        new Map(prev).set(fileId, "Completed ✓"),
      );

      // Clean up progress after 5 seconds
      setTimeout(() => {
        setTranscriptionProgress((prev) => {
          const newMap = new Map(prev);
          newMap.delete(fileId);
          return newMap;
        });
      }, 5000);
    } catch (error) {
      console.error("Transcription error:", error);
      setTranscriptionProgress((prev) => new Map(prev).set(fileId, "Failed ✗"));

      // Clean up error progress after 5 seconds
      setTimeout(() => {
        setTranscriptionProgress((prev) => {
          const newMap = new Map(prev);
          newMap.delete(fileId);
          return newMap;
        });
      }, 5000);
    } finally {
      // Remove from transcribing set
      setTranscribingVideos((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  };

  const extractId = (item) => {
    if (typeof item === "object" && item?._id) {
      return item._id;
    }
    return typeof item === "string" ? item : "";
  };

  const extractIds = (items) => {
    if (!Array.isArray(items)) return [];
    return items.map((item) => extractId(item));
  };

  // Updated useEffect to sync bugStatus from API when it changes
  useEffect(() => {
    if (bugDetailData?.bugStatus) {
      const statusId = extractId(bugDetailData?.bugStatus);
      setBugDetail((prev) => ({
        ...prev,
        bugStatus: statusId,
      }));
    }
  }, [bugDetailData?.bugStatus]);

  const handleUpdateBug = async () => {
    if (hasPendingUploads()) {
      toast.warning(t("Please wait for all uploads to complete before saving"));
      return;
    }

    const failedUploads = bugDetail.attachments.filter(
      (att) => att.uploadFailed,
    );
    if (failedUploads?.length > 0) {
      toast.error(
        t(
          "Some attachments failed to upload. Please remove them or try again.",
        ),
      );
      return;
    }

    const errors: any = {};

    if (!bugDetail?.bugStatus) {
      errors.bugStatus = t("Status is required");
    }

    // Validate story link if required for this type
    const isDefectType =
      bugDetail?.type === "BUG" ||
      bugDetail?.type === "DEFECT" ||
      bugDetail?.type === "SUGGESTIONS";
    const storyRequiredTypes =
      projectDetails?.data?.decryptedData?.storyRequiredTypes || [];
    const isStoryRequiredForType =
      Array.isArray(storyRequiredTypes) &&
      storyRequiredTypes.includes(bugDetail?.type);

    if (isDefectType && isStoryRequiredForType && !selectedStory) {
      setStoryError(t("Story is required"));
      errors.story = t("Story is required");
    } else {
      setStoryError("");
    }

    setValidationErrors(errors);

    if (Object.keys(errors)?.length > 0) {
      return;
    }

    setValidationErrors({});

    setIsUploading(true);

    try {
      const processedAttachments = bugDetail.attachments.map((att) => {
        //server url is key url and url is base64
        const rawUrl = att.serverUrl || att.url;
        const finalUrl =
          typeof rawUrl === "string" && rawUrl.startsWith("https")
            ? rawUrl
            : rawUrl;
        const cleanAttachment = {
          fileName: att.fileName || att.name || "attachment",
          fileType: att.fileType || "application/octet-stream",
          url: finalUrl,
        };

        if (
          cleanAttachment.url &&
          cleanAttachment.url.startsWith("data:") &&
          !att.serverUrl
        ) {
          console.warn(
            `Attachment ${cleanAttachment.fileName} still has base64 URL. This shouldn't happen.`,
          );
        }

        return cleanAttachment;
      });

      const {
        _id,
        summary,
        description,
        severity,
        assignTo,
        bugStatus,
        screenShots,
        project,
        tags,
        type,
        platform,
        startDate,
        dueDate,
        pageUrl,
        estimation,
        isRecurring,
        recurringData,
        requiredSkillSet,
      } = bugDetail;

      const projectId = extractId(project);

      const currentSprintId =
        typeof bugDetail.currentSprint === "object"
          ? bugDetail.currentSprint?._id
          : bugDetail.currentSprint;

      const sprintPayload =
        bugDetail.boardType === "SCRUM"
          ? {
              ...(currentSprintId
                ? { currentSprint: currentSprintId }
                : { isBackLog: true }),
            }
          : {};

      const updatePayload = {
        bugId: _id,
        summary,
        description,
        severity,
        assignTo: extractIds(assignTo),
        pageUrl,
        // updatedAt: new Date(),
        bugStatus,
        screenShots,
        attachments: processedAttachments,
        tags,
        // createdAt,
        type,
        platform,
        jiraConfig: {
          projectKey: bugDetailData?.jiraConfig?.projectKey,
          projectId: projectId,
          projectName:
            typeof bugDetailData?.project === "object"
              ? bugDetailData?.project?.name
              : bugDetailData?.projectName,
          jiraDatas: bugDetail.jiraDatas,
        },
        startDate,
        dueDate,
        estimation,
        project: projectId,
        isRecurring,
        recurringData,
        linkBugs: formatDependenciesForAPI(dependencies),
        ...sprintPayload,
        ...(selectedEpic && {
          epic: selectedEpic === "None" ? null : selectedEpic,
        }),
        ...(requiredSkillSet && {
          requiredSkillSet,
        }),
        ...(bugFor && { bugFor }),
        ...((bugDetail?.type === "BUG" ||
          bugDetail?.type === "DEFECT" ||
          bugDetail?.type === "SUGGESTIONS") &&
        selectedStory
          ? { story: selectedStory }
          : {}),
      };
      await updateBug(updatePayload).unwrap();

      // Updated to use bugDetailData instead of task data
      const apiTaskStatus = extractId(bugDetailData?.bugStatus);
      const statusChanged = apiTaskStatus !== bugStatus;
      const apiTaskProjectId = extractId(bugDetailData?.project);
      const projectChanged = apiTaskProjectId !== projectId;

      if (onTaskUpdate && bugDetailData) {
        const formattedAssignTo = extractIds(assignTo).map((userId) => {
          const user = availableUsers?.find((u) => u._id === userId);
          return user
            ? { _id: userId, fullName: user.fullName, email: user.email }
            : { _id: userId, fullName: "Unknown User" };
        });

        const newStatusObject = availableStatus?.find(
          (status) => status._id === bugStatus,
        );

        const updatedTask = {
          ...bugDetailData,
          summary,
          description,
          severity,
          assignTo: formattedAssignTo,
          bugStatus: newStatusObject || bugDetailData?.bugStatus,
          attachments: processedAttachments,
          tags,
          type,
          startDate,
          dueDate,
          estimation,
          pageUrl,
          // updatedAt: new Date(),
          platform,
          jiraDatas: bugDetail.jiraDatas,
          isRecurring,
          recurringData,
          linkBugs: formatDependenciesForAPI(dependencies),
        };

        if (projectChanged) {
          onTaskUpdate(_id, null);
        } else if (statusChanged) {
          if (bugStatus) {
            onTaskUpdate(_id, updatedTask, bugStatus);
            try {
              await updateBugIndex({
                lexo: true,
                // items: {
                //   _id,
                //   boardIndex: 0,
                //   bugStatus:
                //     newStatusObject?._id ||
                //     bugDetailData.data.decryptedData.bugStatus._id,
                //   project: projectId,
                // },
                project: projectId,
                nextId:
                  columns[newStatusObject._id || bugDetailData?.bugStatus._id]
                    ?.tasks?.[0]?._id || null,
                prevId: null,
                movedId: _id,
              }).unwrap();
            } catch (error) {
              console.error("Error updating bug index:", error);
            }
          } else {
            onTaskUpdate(_id, updatedTask);
          }
        } else {
          onTaskUpdate(_id, updatedTask);
        }
      } else if (refreshColumn) {
        refreshColumn();
      }

      toast.success(t("Task updated successfully"));

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      setBugId("");
      setDrawerOpen(false);
      setActiveTab("1");
      setCreatedBy("");
      setPendingUploads(new Set());
      onClose();
    } catch (error) {
      console.error("Error updating bug:", error);
      toast.error(
        t(
          "Failed to update task",
          error?.data?.message ? `: ${error.data.message}` : "",
        ),
      );

      if (error.message) {
        console.error("Error details:", error.message);
        toast.error(t("Error details") + `: ${error.message}`);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const [deleteBug, { isLoading: isDeleteLoading }] = useDeleteBugMutation();

  const handleDelete = () => {
    deleteBug(bugId)
      .unwrap()
      .then(() => {
        setIsDeleteReply(false);
        toast.success(t("Bug deleted successfully"));
        if (onTaskUpdate) {
          onTaskUpdate(bugId, null);
        } else if (refreshColumn) {
          refreshColumn();
        }

        setBugId("");
        setDrawerOpen(false);
        setActiveTab("1");
        setCreatedBy("");
        setPendingUploads(new Set());
        onClose();
      })
      .catch((error) => {
        console.error("Error deleting task:", error);
        toast.error("Failed to delete task");
        setIsDeleteReply(false);
      });
  };

  const handleRemoveAttachment = (index) => {
    const attachmentToRemove = bugDetail.attachments[index];

    if (attachmentToRemove?.fileName) {
      setPendingUploads((prev) => {
        const newSet = new Set(prev);
        for (const uploadId of newSet as any) {
          if (uploadId.includes(attachmentToRemove.fileName)) {
            newSet.delete(uploadId);
          }
        }
        return newSet;
      });
    }

    const isScreenshotFile =
      attachmentToRemove?.fileName === "full-page-screenshot.png" ||
      attachmentToRemove?.fileName === "element-screenshot.png";

    setBugDetail((prev) => ({
      ...prev,
      attachments: [
        ...prev.attachments.slice(0, index),
        ...prev.attachments.slice(index + 1),
      ],
    }));

    if (isScreenshotFile) {
      if (screenshotMap[attachmentToRemove.fileName]) {
        const screenshotIndex =
          screenshotMap[attachmentToRemove.fileName].index;
        setImageUrls((prev) => prev.filter((_, i) => i !== screenshotIndex));

        setScreenshotMap((prev) => {
          const newMap = { ...prev };
          delete newMap[attachmentToRemove.fileName];
          return newMap;
        });
      }
    }

    toast.success(t("Attachment removed successfully"));
  };

  const handleRemoveScreenshot = () => {
    setBugDetail((prev) => ({
      ...prev,
      attachments: prev.attachments.filter(
        (attachment) =>
          attachment.fileName !== "full-page-screenshot.png" &&
          attachment.fileName !== "element-screenshot.png",
      ),
    }));

    setImageUrls([]);
    setScreenshotMap({});

    toast.success(t("Screenshots removed successfully"));
  };

  const handleViewAttachment = (attachment) => {
    // If it's a video, show the video preview panel
    if (attachment.type === "video" && attachment.url) {
      setPreviewVideoUrl(attachment.url);
      setIsVideoPreviewOpen(true);
    } else {
      // Otherwise show the standard attachment view modal
      setSelectedAttachment(attachment);
      setIsAttachmentViewOpen(true);
    }
  };

  const handleAssigneeChange = (_, newValues) => {
    setBugDetail((prev) => ({
      ...prev,
      assignTo: newValues.map((user) => user._id),
    }));
  };

  const handleSaveAnnotation = async (annotatedImageData) => {
    const uniqueId = new Date().getTime();
    try {
      const fileName = view
        ? "full-page-screenshot.png"
        : "element-screenshot.png";
      const name = `${fileName.replace(".png", "")}_${uniqueId}.png`;

      const url = await uploadScreenshot(annotatedImageData, null, name);

      setBugDetail((prev) => ({
        ...prev,
        attachments: prev.attachments.map((attachment) =>
          attachment.fileName === fileName
            ? { ...attachment, url }
            : attachment,
        ),
      }));

      const updatedImageUrls = [...imageUrls];
      if (screenshotMap[fileName]) {
        updatedImageUrls[screenshotMap[fileName].index] = annotatedImageData;
      } else {
        updatedImageUrls[view ? 1 : 0] = annotatedImageData;
      }
      setImageUrls(updatedImageUrls);
    } catch (error) {
      console.error("Error uploading annotated image:", error);
      toast.error(t("Failed to save annotation"));
    } finally {
      setIsAnnotationEditorOpen(false);
    }
  };

  const handleAnnotateAttachment = (index) => {
    const attachment = bugDetail.attachments[index];

    if (attachment.url && attachment.url.startsWith("data:")) {
      setCurrentAttachmentIndex(index);
      setIsAnnotatingAttachment(true);
    } else if (attachment.tempBase64) {
      setCurrentAttachmentIndex(index);
      setIsAnnotatingAttachment(true);
    } else if (attachment.serverUrl) {
      setIsUploading(true);

      fetch(attachment.serverUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64Data = reader.result;

            const updatedAttachments = [...bugDetail.attachments];
            updatedAttachments[index] = {
              ...updatedAttachments[index],
              tempBase64: base64Data,
            };

            setBugDetail((prev) => ({
              ...prev,
              attachments: updatedAttachments,
            }));

            setCurrentAttachmentIndex(index);
            setIsAnnotatingAttachment(true);
            setIsUploading(false);
          };
          reader.readAsDataURL(blob);
        })
        .catch((error) => {
          console.error("Error fetching image for annotation:", error);
          toast.error(t("Failed to load image for annotation"));
          setIsUploading(false);
        });
    } else {
      setIsUploading(true);

      postAttachmentKey({
        key: attachment.url,
      })
        .unwrap()
        .then((res) => {
          if (res?.data?.decryptedData?.url) {
            const imageUrl = res.data.decryptedData.url;

            fetch(imageUrl)
              .then((response) => response.blob())
              .then((blob) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64Data = reader.result;

                  const updatedAttachments = [...bugDetail.attachments];
                  updatedAttachments[index] = {
                    ...updatedAttachments[index],
                    tempBase64: base64Data,
                  };

                  setBugDetail((prev) => ({
                    ...prev,
                    attachments: updatedAttachments,
                  }));

                  setCurrentAttachmentIndex(index);
                  setIsAnnotatingAttachment(true);
                  setIsUploading(false);
                };
                reader.readAsDataURL(blob);
              })
              .catch((error) => {
                console.error("Error converting image to base64:", error);
                toast.error(t("Failed to prepare image for annotation"));
                setIsUploading(false);
              });
          } else {
            toast.error(t("Failed to get image URL"));
            setIsUploading(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching attachment URL:", error);
          toast.error(t("Failed to get image URL"));
          setIsUploading(false);
        });
    }
  };

  const handleSaveAttachmentAnnotation = async (annotatedImageData) => {
    try {
      const attachment = bugDetail.attachments[currentAttachmentIndex];

      const uniqueId = new Date().getTime();
      const randomId = Math.random().toString(36).substring(2);
      const originalFileName =
        attachment.fileName || attachment.name || "attachment.png";
      const annotatedFileName = `annotated_${uniqueId}_${randomId}_${originalFileName}`;

      const updatedAttachments = [...bugDetail.attachments];

      updatedAttachments[currentAttachmentIndex] = {
        ...attachment,
        url: annotatedImageData,
        tempBase64: annotatedImageData,
        isAnnotated: true,
        isUploading: true,
        fileName: annotatedFileName,
      };

      setBugDetail((prev) => ({
        ...prev,
        attachments: updatedAttachments,
      }));

      const uploadUrl = await uploadScreenshot(
        annotatedImageData,
        undefined,
        annotatedFileName,
      );

      const finalAttachments = [...bugDetail.attachments];
      finalAttachments[currentAttachmentIndex] = {
        ...finalAttachments[currentAttachmentIndex],
        url: annotatedImageData,
        serverUrl: uploadUrl,
        isUploading: false,
      };

      setBugDetail((prev) => ({
        ...prev,
        attachments: finalAttachments,
      }));

      await updateBugAttachments(finalAttachments);

      toast.success(t("Annotation saved successfully"));
    } catch (error) {
      console.error("Error saving annotated attachment:", error);
      toast.error(t("Failed to save annotation"));

      const errorAttachments = [...bugDetail.attachments];
      errorAttachments[currentAttachmentIndex] = {
        ...errorAttachments[currentAttachmentIndex],
        isUploading: false,
        uploadFailed: true,
      };

      setBugDetail((prev) => ({
        ...prev,
        attachments: errorAttachments,
      }));
    } finally {
      setIsAnnotatingAttachment(false);
      setCurrentAttachmentIndex(null);
    }
  };

  const updateBugAttachments = async (attachments) => {
    try {
      const processedAttachments = attachments.map((att) => {
        const rawUrl = att.serverUrl || att.url;
        const finalUrl =
          typeof rawUrl === "string" && rawUrl.startsWith("https")
            ? rawUrl
            : rawUrl;

        return {
          fileName: att.fileName || att.name || "attachment",
          fileType: att.fileType || "application/octet-stream",
          url: finalUrl,
        };
      });

      const projectId = extractId(bugDetail.project);

      const updatePayload = {
        bugId: bugDetail._id,
        attachments: processedAttachments,
        summary: bugDetail.summary,
        description: bugDetail.description,
        severity: bugDetail.severity,
        assignTo: extractIds(bugDetail.assignTo),
        bugStatus: bugDetail.bugStatus,
        screenShots: bugDetail.screenShots,
        tags: bugDetail.tags,
        createdAt: bugDetail.createdAt,
        type: bugDetail.type,
        platform: bugDetail.platform,
        startDate: bugDetail.startDate,
        dueDate: bugDetail.dueDate,
        estimation: bugDetail.estimation,
        project: projectId,
        pageUrl: bugDetail.pageUrl,
        updatedAt: new Date(),
        linkBugs: formatDependenciesForAPI(dependencies),
        jiraConfig: {
          projectKey: bugDetailData?.jiraConfig?.projectKey,
          projectId: projectId,
          projectName:
            typeof bugDetailData?.project === "object"
              ? bugDetailData?.project?.name
              : bugDetailData?.projectName,
          jiraDatas: bugDetail.jiraDatas,
        },
        isRecurring: bugDetail.isRecurring,
        recurringData: bugDetail.recurringData,
      };

      await updateBug(updatePayload).unwrap();
    } catch (error) {
      console.error("❌ Error updating attachments in backend:", error);
      throw error;
    }
  };

  const base64ToBlob = (base64String) => {
    const byteCharacters = atob(base64String.split(",")[1]);
    const byteArrays = [];
    for (let i = 0; i < byteCharacters?.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }
    return new Blob([new Uint8Array(byteArrays)], { type: "image/png" });
  };

  const uploadScreenshot = async (base64Screenshot, blobData, name) => {
    if (!base64Screenshot && !blobData) return null;

    const finalBlob = blobData || base64ToBlob(base64Screenshot);

    const formData = new FormData();
    formData.append("file", finalBlob, name);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_COMPANY_API_URL}/file/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        },
      );
      return response?.data?.data?.localData?.Key || null;
    } catch (error) {
      console.error("Screenshot upload failed:", error);
      return null;
    }
  };

  const handleClickHere = () => {
    onClose();
    navigate("/company-settings/settings/integration");
  };

  const getDefaultFields = (jiraProjectKey) => {
    const jiraConfig = projectDetails?.data?.decryptedData?.jiraConfig;

    // Check if jiraConfig exists and is an array
    if (!jiraConfig || !Array.isArray(jiraConfig)) {
      return null;
    }

    const selectedProject = jiraConfig.find(
      (project) => project.key === jiraProjectKey,
    );

    return selectedProject ? selectedProject.defaultFields : null;
  };

  const defaultFields = getDefaultFields(bugDetailData?.projectKey);

  const prepareCombinedAttachments = async () => {
    setCombiningAttachments(true);
    const combined = [];

    try {
      if (bugDetailData?.videoRecordings?.length > 0) {
        bugDetailData?.videoRecordings.forEach((video, index) => {
          combined.push({
            _id: `video_${index}`,
            name: video.fileName || `Video Recording ${index + 1}`,
            type: "video",
            fileType: "video/mp4",
            size: video.size || "N/A",
            date: bugDetail.createdAt,
            url: videoUrl,
            originalIndex: -1,
            isVideo: true,
          });
        });
      }

      imageUrls.forEach((imageUrl, index) => {
        const fileName = Object.keys(screenshotMap).find(
          (key) => screenshotMap[key].index === index,
        );

        combined.push({
          _id: `screenshot_${index}`,
          name: fileName || `Screenshot ${index + 1}`,
          type: "screenshot",
          fileType: "image/png",
          size: "N/A",
          date: bugDetail.createdAt,
          url: imageUrl,
          originalIndex: index,
          isScreenshot: true,
        });
      });

      const attachmentPromises =
        bugDetail.attachments?.map(async (attachment, index) => {
          if (
            attachment.fileName !== "full-page-screenshot.png" &&
            attachment.fileName !== "element-screenshot.png"
          ) {
            let displayUrl = attachment.url;
            if (attachment?.url?.startsWith("https:")) {
              displayUrl = attachment.url;
            }
            if (
              attachment.url &&
              !attachment.url.startsWith("data:") &&
              !attachment.url.startsWith("http")
            ) {
              try {
                const response = await postAttachmentKey({
                  key: attachment.url,
                }).unwrap();
                displayUrl =
                  response?.data?.decryptedData?.url || attachment.url;
              } catch (error) {
                console.error("Error fetching attachment URL:", error);
                displayUrl = attachment.url;
              }
            }

            return {
              _id: `attachment_${index}`,
              name:
                attachment.fileName ||
                attachment.name ||
                `Attachment ${index + 1}`,
              type: attachment.fileType?.startsWith("image/")
                ? "image"
                : "file",
              fileType: attachment.fileType || "application/octet-stream",
              size: attachment.size || "N/A",
              date: bugDetail.updatedAt || bugDetail.createdAt,
              url: displayUrl,
              serverUrl: attachment.serverUrl,
              tempBase64: attachment.tempBase64,
              isUploading: attachment.isUploading,
              originalIndex: index,
              ...attachment,
            };
          }
          return null;
        }) || [];

      const resolvedAttachments = await Promise.all(attachmentPromises);

      resolvedAttachments.filter(Boolean).forEach((attachment) => {
        combined.push(attachment);
      });
    } catch (error) {
      console.error("Error preparing combined attachments:", error);
    } finally {
      setCombiningAttachments(false);
    }

    return combined;
  };

  // Updated useEffect to use bugId instead of task dependency
  useEffect(() => {
    if (open && bugId) {
      prepareCombinedAttachments().then(setCombinedAttachments);
    }
  }, [
    open,
    bugId,
    bugDetailData,
    bugDetail.attachments,
    imageUrls,
    videoUrl,
    screenshotMap,
  ]);

  useEffect(() => {
    if (Array.isArray(configData) && configData?.length > 0) {
      const findJiraConfigExist = configData.find(
        (config) => config.type === "JIRA",
      );
      if (findJiraConfigExist) {
        setIsJiraConfigured(true);
        setBugDetail({
          ...bugDetail,
          platform: "JIRA",
        });
      } else {
        setIsJiraConfigured(false);
        setBugDetail({
          ...bugDetail,
          platform: "NONE",
        });
      }
    } else {
      setIsJiraConfigured(false);
    }
  }, [configData]);

  // Updated useEffect to use bugId instead of task dependency
  useEffect(() => {
    const rawUrl = bugDetailData?.videoRecordings?.[0]?.url;
    if (!rawUrl) return;

    // Case 1: already public URL (https)
    if (rawUrl.startsWith("https")) {
      setVideoUrl(rawUrl);
      return;
    }

    // Case 2: S3 key (dev/..., UI_AUTOMATION/..., etc.)
    postAttachmentKey({ key: rawUrl })
      .then((res) => {
        const url = res?.data?.data?.decryptedData?.url;
        if (url) setVideoUrl(url);
      })
      .catch((err) => {
        console.error("Error fetching video URL", err);
      });
  }, [open, bugId, bugDetail, bugDetailData?.videoRecordings?.[0]?.url]);

  useEffect(() => {
    const fetchStatusLabels = async () => {
      if (bugDetailData?.jiraConfig?.projectKey) {
        try {
          const response = await getProjectStatusLabels({
            keys: [bugDetailData?.jiraConfig?.projectKey],
          });
          if (response?.data?.statuses) {
            setStatusList(response.data.statuses);
          }
        } catch (err) {
          console.error("Error fetching status labels:", err);
        }
      }
    };

    fetchStatusLabels();
  }, [bugDetailData?.jiraConfig?.projectKey]);

  // Optimized useEffect: Populates fields immediately, fetches logs in background
  useEffect(() => {
    // 1. Define Screenshot Fetching Logic
    const fetchScreenshots = async () => {
      if (bugDetailData?.attachments?.length) {
        const screenshots = bugDetailData?.attachments?.filter((x) =>
          ["element-screenshot.png", "full-page-screenshot.png"].includes(
            x.fileName,
          ),
        );

        if (screenshots?.length === 0) return;

        const screenshotUrls = [];

        for (const screenshot of screenshots) {
          try {
            const response = await postAttachmentKey({
              key: screenshot.url,
            }).unwrap();
            const imageUrl = response?.data?.decryptedData?.url;

            const imageResponse = await fetch(imageUrl);
            const blob = await imageResponse.blob();

            const base64Data = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });

            screenshotUrls.push({
              fileName: screenshot.fileName,
              url: base64Data,
            });
          } catch (error) {
            console.error("Error fetching image:", error);
          }
        }

        const base64Images = screenshotUrls.map((item) => item.url);
        setImageUrls(base64Images);

        setScreenshotMap(
          screenshotUrls.reduce((acc, item, index) => {
            acc[item.fileName] = {
              index,
              url: item.url,
            };
            return acc;
          }, {}),
        );
      }
    };

    // 2. Define Main Process Logic
    const processBugDetails = async () => {
      if (bugDetailData) {
        // --- Dependency Parsing ---
        const linkBugs = bugDetailData?.linkBugs || [];
        const parsedDependencies = parseLinkBugs(linkBugs);
        setDependencies(parsedDependencies);

        const jiraProjectKey = bugDetailData?.jiraConfig?.projectKey;

        // --- STEP 1: IMMEDIATE UPDATE (Synchronous) ---
        // Populate inputs immediately. Use empty array for logs if they need fetching.
        setBugDetail((prev) => {
          const apiStatus = bugDetailData?.bugStatus;

          // Always use the API status when available to ensure fresh data
          let finalStatus;
          if (apiStatus) {
            finalStatus = extractId(apiStatus);
          } else {
            finalStatus = "";
          }

          const estimationFromApi = bugDetailData?.estimation || "";
          const { days, hours, minutes } = parseEstimation(estimationFromApi);

          const apiRecurringData = bugDetailData?.recurringData;
          const apiIsRecurring = bugDetailData?.isRecurring || false;

          // Update Recurring State separately
          if (apiIsRecurring && apiRecurringData) {
            const frontendRecurringConfig =
              convertFromBackendRecurringData(apiRecurringData);
            setRecurringConfig(frontendRecurringConfig);
            setIsRecurring(true);
          } else {
            setIsRecurring(false);
          }

          const projectData = bugDetailData?.project;
          const boardType =
            typeof projectData === "object" ? projectData?.boardType || "" : "";
          const currentSprintFromAPI = bugDetailData?.currentSprint;

          return {
            ...prev,
            ...bugDetailData,
            projectKey: jiraProjectKey || "",
            project: bugDetailData?.project,
            estimation: estimationFromApi,
            estimationDays: days,
            estimationHours: hours,
            estimationMinutes: minutes,
            startDate: bugDetailData?.startDate || "",
            bugStatus: finalStatus,
            assignTo: extractIds(bugDetailData?.assignTo || []),
            isRecurring: apiIsRecurring,
            recurringData: apiRecurringData,
            boardType: boardType,
            currentSprint: currentSprintFromAPI || null,
            isBackLog: bugDetailData?.isBackLog || false,
            // If logs are arrays, use them. If strings (URLs), use empty [] for now.
            logs: {
              console: Array.isArray(bugDetailData?.logs?.console)
                ? bugDetailData.logs.console
                : [],
              network: Array.isArray(bugDetailData?.logs?.network)
                ? bugDetailData.logs.network
                : [],
              actions: Array.isArray(bugDetailData?.logs?.actions)
                ? bugDetailData.logs.actions
                : [],
            },
          };
        });

        // --- STEP 2: ASYNC LOG FETCHING ---
        // Fetch logs in background and update state when ready
        let consoleLogsData = [];
        let networkLogsData = [];
        let actionLogsData = [];
        let hasNewLogs = false;

        try {
          // 1. Handle Console Logs
          if (
            typeof bugDetailData?.logs?.console === "string" &&
            bugDetailData.logs.console.trim().length > 0
          ) {
            try {
              consoleLogsData = JSON.parse(bugDetailData.logs.console);
              hasNewLogs = true;
            } catch (e) {
              const val = bugDetailData.logs.console.trim();
              if (val.startsWith("https")) {
                try {
                  const logRes = await fetch(val);
                  consoleLogsData = await logRes.json();
                  hasNewLogs = true;
                } catch (fetchErr) {
                  console.error("Error fetching console logs public", fetchErr);
                }
              } else {
                try {
                  const res = await postAttachmentKey({ key: val }).unwrap();
                  const url = res?.data?.decryptedData?.url;
                  if (url) {
                    const logRes = await fetch(url);
                    consoleLogsData = await logRes.json();
                    hasNewLogs = true;
                  }
                } catch (fetchErr) {
                  console.error("Error fetching console logs S3", fetchErr);
                }
              }
            }
          } else if (Array.isArray(bugDetailData?.logs?.console)) {
            consoleLogsData = bugDetailData.logs.console;
          }

          // 2. Handle Network Logs
          if (
            typeof bugDetailData?.logs?.network === "string" &&
            bugDetailData.logs.network.trim().length > 0
          ) {
            try {
              networkLogsData = JSON.parse(bugDetailData.logs.network);
              hasNewLogs = true;
            } catch (e) {
              const val = bugDetailData.logs.network.trim();
              if (val.startsWith("https")) {
                try {
                  const logRes = await fetch(val);
                  networkLogsData = await logRes.json();
                  hasNewLogs = true;
                } catch (fetchErr) {
                  console.error("Error fetching network logs public", fetchErr);
                }
              } else {
                try {
                  const res = await postAttachmentKey({ key: val }).unwrap();
                  const url = res?.data?.decryptedData?.url;
                  if (url) {
                    const logRes = await fetch(url);
                    networkLogsData = await logRes.json();
                    hasNewLogs = true;
                  }
                } catch (fetchErr) {
                  console.error("Error fetching network logs S3", fetchErr);
                }
              }
            }
          }
          // 3. Handle Action Logs
          if (
            typeof bugDetailData?.logs?.actions === "string" &&
            bugDetailData.logs.actions.trim().length > 0
          ) {
            try {
              actionLogsData = JSON.parse(bugDetailData.logs.actions);
              hasNewLogs = true;
            } catch (e) {
              const val = bugDetailData.logs.actions.trim();
              if (val.startsWith("https")) {
                try {
                  const logRes = await fetch(val);
                  actionLogsData = await logRes.json();
                  hasNewLogs = true;
                } catch (fetchErr) {
                  console.error("Error fetching action logs public", fetchErr);
                }
              } else {
                try {
                  const res = await postAttachmentKey({ key: val }).unwrap();
                  const url = res?.data?.decryptedData?.url;
                  if (url) {
                    const logRes = await fetch(url);
                    actionLogsData = await logRes.json();
                    hasNewLogs = true;
                  }
                } catch (fetchErr) {
                  console.error("Error fetching action logs S3", fetchErr);
                }
              }
            }
          } else if (Array.isArray(bugDetailData?.logs?.network)) {
            networkLogsData = bugDetailData.logs.network;
          }
        } catch (error) {
          console.error("Error processing logs:", error);
        }

        // --- STEP 3: UPDATE LOGS IF FETCHED ---
        if (hasNewLogs) {
          setBugDetail((prev) => ({
            ...prev,
            logs: {
              console: consoleLogsData,
              network: networkLogsData,
              actions: actionLogsData,
            },
          }));
        }

        // Trigger screenshot fetch independently
        await fetchScreenshots();
      }
    };

    processBugDetails();
  }, [bugDetailData]); // Removed other dependencies to avoid infinite loops
  useEffect(() => {
    if (jiraprojectList?.length > 0 && defaultFields) {
      const initialFormData = {};

      Object.entries(defaultFields).forEach(([, fieldData]) => {
        const fieldKey = Object.keys(fieldData)[0];
        initialFormData[fieldKey] = fieldData[fieldKey];
      });

      setBugDetail((prev) => ({
        ...prev,
        jiraDatas: {
          ...prev.jiraDatas,
          ...initialFormData,
        },
      }));
    }
  }, [jiraprojectList, defaultFields]);

  useEffect(() => {
    if (bugDetail?.bugStatus && availableStatus?.length > 0) {
      const isCurrentStatusAvailable = availableStatus.some(
        (status) => status._id === bugDetail.bugStatus,
      );

      if (!isCurrentStatusAvailable) {
        setBugDetail((prev) => ({
          ...prev,
          bugStatus: "",
        }));
      }
    }
  }, [availableStatus, bugDetail?.bugStatus]);

  useEffect(() => {
    if (open) {
      setActiveTab("1");
      setPendingUploads(new Set());
      setTimeLogs({});
    }
  }, [open]);

  // Initialize selectedStory from bugDetailData when drawer opens
  useEffect(() => {
    if (bugDetailData) {
      const storyId =
        typeof bugDetailData?.story === "object"
          ? bugDetailData?.story?._id
          : bugDetailData?.story;
      setSelectedStory(storyId || null);
    }
  }, [bugDetailData]);

  const handleRichTextEditor = (e) => {
    setBugDetail((prev) => ({
      ...prev,
      description: e,
    }));
  };

  const stripHtmlTags = (html) => {
    if (!html) return "";
    return html.replace(/<\/?[^>]+(>|$)/g, "");
  };

  const handleAIImprove = async (transcript: string): Promise<string> => {
    if (!isAiConfigured) {
      setShowAiConfigDialog(true);
      return "";
    }
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_CHAT_API_URL}/api/agents/task-refiner/generate-text`,
        {
          prompt: transcript,
          store: false,
          module: "general",
          businessApplication:
            storageService.get("businessApplication") || "123",
          team: storageService.get("team") || "111",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        },
      );
      if (response?.data?.text) {
        return response.data.text;
      }
      return transcript;
    } catch (error) {
      console.error("Error improving text with AI:", error);
      toast.error(t("Failed to improve text with AI"));
      return transcript;
    }
  };

  const handleAudioAttachment = async (audioBlob: Blob, transcript: string) => {
    try {
      // Generate unique filename for the audio
      const timestamp = Date.now();
      const uniqueFileName = `voice_recording_${timestamp}.webm`;

      // Create File from Blob (matching processFiles pattern)
      const audioFile = new File([audioBlob], uniqueFileName, {
        type: "audio/webm",
      });

      // Upload audio file
      const formData = new FormData();
      formData.append("file", audioFile, uniqueFileName);

      const response = await axios.post(
        `${import.meta.env.VITE_COMPANY_API_URL}/file/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        },
      );

      const uploadUrl = response?.data?.data?.localData?.Key || null;
      if (!uploadUrl) {
        throw new Error("Failed to upload audio file");
      }

      // Add audio as attachment (matching the structure in processFiles)
      const newAttachment = {
        fileType: "audio/webm",
        fileName: uniqueFileName,
        url: uploadUrl,
        serverUrl: uploadUrl,
        isUploading: false,
        uploadFailed: false,
      };

      setBugDetail((prev) => ({
        ...prev,
        attachments: [...(prev.attachments || []), newAttachment],
      }));

      toast.success(t("Audio recording saved successfully"));
    } catch (error) {
      console.error("Error uploading audio:", error);
      toast.error(t("Failed to save audio recording"));
    }
  };

  const getCreatorName = () => {
    const createdByData = bugDetailData?.createdBy;
    if (typeof createdByData === "object" && createdByData?.fullName) {
      return createdByData.fullName;
    }
    return createdBy || "Unknown User";
  };

  const getRecurringDisplayText = () => {
    if (!isRecurring || !recurringConfig.isRecurring) return "";

    const frequency = recurringConfig.frequency;
    const trigger = recurringConfig.trigger;

    return `${frequency} - ${trigger}`;
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        onPaste={handleSystemPaste}
        sx={{
          "& .MuiDrawer-paper": {
            width: "90vw",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            px: 5,
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: "sticky",
            top: 0,
            zIndex: 10,
            background:
              "linear-gradient(90deg, rgba(59,130,246,0.08) 0%, rgba(236,72,153,0.08) 100%)",
            backdropFilter: "blur(6px)",
            boxShadow: "0 4px 16px rgba(15, 23, 42, 0.08)",
            borderRadius: "0 0 12px 12px",
          }}
        >
          <Box display="flex" alignItems="center">
            <Typography
              variant="h6"
              sx={{
                color: "text.primary",
                fontWeight: 700,
                mr: 2,
                letterSpacing: "0.2px",
              }}
            >
              {t("Task Details")}
              {bugDetailData?.jiraKey && (
                <span
                  style={{
                    fontSize: "0.7rem",
                    marginLeft: "5px",
                    color: theme.palette.text.secondary,
                  }}
                >
                  ({bugDetailData?.jiraKey})
                </span>
              )}
            </Typography>
            <TabContext value={activeTab}>
              <Box sx={{ borderBottom: 0, borderColor: "divider" }}>
                <TabList
                  onChange={handleTabChange}
                  TabIndicatorProps={{ style: { display: "none" } }}
                  sx={{
                    height: "100%",
                    minHeight: "unset",
                    "& .MuiTab-root": {
                      textTransform: "none",
                      minHeight: "27px",
                      height: "27px",
                      marginRight: 0,
                      border: `1px solid ${theme.palette.divider}`,
                      color: "text.primary",
                      transition: "all 0.2s ease",
                    },
                    "& .Mui-selected": {
                      backgroundColor: (theme) =>
                        hexToRgba(theme.palette.primary.main, 0.05),
                      fontWeight: "bold",
                      border: (theme) =>
                        `1.5px solid ${theme.palette.primary.main}`,
                      boxShadow: "0 4px 10px rgba(25, 118, 210, 0.15)",
                      "&:first-child": {
                        borderRadius: "4px 0px 0px 4px",
                      },
                      "&:last-child": {
                        borderRadius: "0px 4px 4px 0px",
                      },
                    },
                  }}
                >
                  <Tab label={t("Overview")} value="1" />
                  {!isPmoHub &&
                    bugDetail?.type !== "EPIC" &&
                    (videoUrl ||
                      combinedAttachments.some((att) =>
                        att.fileType?.startsWith("image/"),
                      )) && <Tab label={t("Diagnostics")} value="2" />}
                  {!isPmoHub &&
                    bugDetail?.type !== "EPIC" &&
                    // !isCurrentUserClient && // Aitomation tab will show when video available
                    videoUrl 
                    && (
                      <Tab label={t("Automation")} value="3" />
                    )}
                  {!isPmoHub &&
                    bugDetail?.type !== "EPIC" &&
                    !isCurrentUserClient && (
                      <Tab label={t("Customer Panel")} value="4" />
                    )}
                  {!isPmoHub && <Tab label={t("Bug Analyzer")} value="6" />}
                </TabList>
              </Box>
            </TabContext>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Button variant="outlined" onClick={onClose} size="small">
              {t("Cancel")}
            </Button>
            {(!isCurrentUserClient || bugDetail?.bugFor !== "BOARD") && (
              <Tooltip
                title={
                  saveDisabledReasons.length > 0
                    ? saveDisabledReasons.join(" • ")
                    : t("Save")
                }
              >
                <span>
                  <LoadingButton
                    variant="contained"
                    onClick={handleUpdateBug}
                    loading={
                      updatingBug || isLoading || isUploading || isCreatingTag
                    }
                    disabled={saveDisabledReasons.length > 0}
                    size="small"
                  >
                    {hasPendingUploads() ? t("Uploading...") : t("Save")}
                  </LoadingButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 3,
          }}
        >
          {isLoading || isLoadingConfig ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <TabContext value={activeTab}>
              <TabPanel value="1" sx={{ p: 0 }}>
                {/* Two Column Layout - 70% / 30% */}
                <Grid2 container spacing={3} sx={{ mb: 3 }}>
                  {/* Left Grid - 70% */}
                  <Grid2 size={8}>
                    {/* Task Key and Summary */}
                    <Box
                      sx={{
                        mb: 3,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 2,
                        p: 1.5,
                        boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
                      }}
                    >
                      <Box sx={{ position: "relative" }}>
                        <TextField
                          placeholder={t("Add title")}
                          name="summary"
                          disabled={
                            isCurrentUserClient && bugDetail?.bugFor === "BOARD"
                          }
                          value={bugDetail?.summary}
                          onChange={(e) => {
                            setBugDetail((prev) => ({
                              ...prev,
                              summary: e.target.value.slice(0, SUMMARY_MAX),
                            }));
                          }}
                          variant="outlined"
                          fullWidth
                          label={t("Title")}
                          size="small"
                          inputProps={{ maxLength: SUMMARY_MAX }}
                          helperText={`${bugDetail?.summary?.length || 0} / ${SUMMARY_MAX}`}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1,
                              paddingLeft: 0,
                              position: "relative",
                              overflow: "hidden",
                            },
                            "& .MuiOutlinedInput-input": {
                              paddingLeft: bugDetail?.bugKey ? 1.5 : undefined,
                              color:
                                isVoiceRecording || isVoiceFinalizing
                                  ? "transparent"
                                  : "inherit",
                            },
                            marginTop: 1,
                          }}
                          InputProps={{
                            startAdornment: (
                              <>
                                {bugDetail?.bugKey ? (
                                  <InputAdornment
                                    position="start"
                                    sx={{ mr: 0, ml: 0 }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        px: 1.5,
                                        py: 1,
                                        borderRight: "1px solid",
                                        borderColor: "divider",
                                        backgroundColor:
                                          theme.palette.mode === "dark"
                                            ? theme.palette.background.paper
                                            : "#f8fafc",
                                        minWidth: "fit-content",
                                        height: "100%",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "text.secondary",
                                          fontWeight: 500,
                                          fontSize: "0.875rem",
                                        }}
                                      >
                                        {bugDetail.bugKey}
                                      </Typography>
                                    </Box>
                                  </InputAdornment>
                                ) : null}
                              </>
                            ),
                            endAdornment: (
                              <InputAdornment position="end" sx={{ zIndex: 2 }}>
                                {(!isCurrentUserClient ||
                                  bugDetail?.bugFor !== "BOARD") && (
                                  <SummaryVoiceRecorder
                                    onTranscriptChange={(transcript) => {
                                      setBugDetail((prev) => ({
                                        ...prev,
                                        summary: transcript.slice(
                                          0,
                                          SUMMARY_MAX,
                                        ),
                                      }));
                                    }}
                                    onAIImprove={handleAIImprove}
                                    currentText={bugDetail?.summary || ""}
                                    maxLength={SUMMARY_MAX}
                                    language="en-US"
                                    onRecordingChange={setIsVoiceRecording}
                                    onFinalizingChange={setIsVoiceFinalizing}
                                    onProcessingMessageChange={
                                      setSummaryProcessingMessage
                                    }
                                  />
                                )}
                              </InputAdornment>
                            ),
                          }}
                        />

                        {/* Processing Message Overlay for Summary */}
                        {summaryProcessingMessage && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: "38%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(30, 30, 30, 0.98)"
                                  : "rgba(255, 255, 255, 0.98)",
                              padding: "6px 12px",
                              borderRadius: "16px",
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
                              zIndex: 10,
                              border: `1px solid ${theme.palette.divider}`,
                              pointerEvents: "none",
                            }}
                          >
                            <CircularProgress size={14} thickness={4} />
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {summaryProcessingMessage}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Description Section */}

                      <Box
                        sx={{
                          maxHeight: "250px",
                          overflow: "auto",
                          borderRadius: 1,
                          mt: 2,
                          display: "flex",
                          flexDirection: "column",
                          "& .editor-container": {
                            height: "100% !important",
                            maxHeight: "100% !important",
                          },
                          "& .quill": {
                            height: "100% !important",
                            display: "flex !important",
                            flexDirection: "column !important",
                          },
                          "& .ql-container": {
                            flex: "1 !important",
                            overflow: "auto !important",
                          },
                          "& .ql-editor": {
                            height: "auto !important",
                            maxHeight: "none !important",
                            overflow: "auto !important",
                          },
                        }}
                      >
                        <RichTextEditor
                          onChange={handleRichTextEditor}
                          value={bugDetail?.description}
                          placeholder={t("Enter your description here...")}
                          readOnly={
                            isCurrentUserClient && bugDetail?.bugFor === "BOARD"
                          }
                          style={{ header: false } as React.CSSProperties}
                          enableVoiceRecording={
                            !isCurrentUserClient ||
                            bugDetail?.bugFor !== "BOARD"
                          }
                          onAudioAttachment={handleAudioAttachment}
                          onAIImprove={handleAIImprove}
                          maxLength={DESCRIPTION_MAX}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        color={
                          (stripHtmlTags(bugDetail?.description)?.length || 0) >
                          DESCRIPTION_MAX
                            ? "error"
                            : "textSecondary"
                        }
                        sx={{ display: "block", mt: 1 }}
                      >
                        {stripHtmlTags(bugDetail?.description)?.length || 0} /{" "}
                        {DESCRIPTION_MAX}
                      </Typography>
                    </Box>

                    {/* Schedule Section */}
                    {bugDetail?.type !== "EPIC" &&
                      bugDetail?.boardType === "SCRUM" && (
                        <Box
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: 2,
                            p: 2.5,
                            mb: 2,
                            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
                            border: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                              mb: 2,
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            {t("Schedule")}
                          </Typography>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 2,
                            }}
                          >
                            <StyledTextField
                              label={t("Start Date")}
                              type="date"
                              value={bugDetail?.startDate || ""}
                              onChange={(e) =>
                                setBugDetail((prev) => ({
                                  ...prev,
                                  startDate: e.target.value,
                                }))
                              }
                              size="small"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                            />

                            <StyledTextField
                              label={t("Due Date")}
                              type="date"
                              value={bugDetail?.dueDate || ""}
                              onChange={(e) =>
                                setBugDetail((prev) => ({
                                  ...prev,
                                  dueDate: e.target.value,
                                }))
                              }
                              size="small"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                            />
                          </Box>
                        </Box>
                      )}

                    {/* Attachments & Screenshots Section */}
                    <Accordion
                      defaultExpanded
                      sx={{
                        mb: 3,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: "8px !important",
                        boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
                        "&:before": {
                          display: "none",
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          "& .MuiAccordionSummary-content": {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            margin: "12px 0",
                          },
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, color: "text.primary" }}
                        >
                          {t("Attachments & Screenshots")} (
                          {combinedAttachments.length})
                        </Typography>
                        <ToggleButtonGroup
                          value={attachmentView}
                          exclusive
                          onChange={(_, newView) => {
                            if (newView !== null) {
                              setAttachmentView(newView);
                            }
                          }}
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title={t("Grid view")}>
                            <ToggleButton value="grid" aria-label="grid view">
                              <ViewModuleIcon />
                            </ToggleButton>
                          </Tooltip>
                          <Tooltip title={t("List view")}>
                            <ToggleButton value="list" aria-label="list view">
                              <ViewListIcon />
                            </ToggleButton>
                          </Tooltip>
                        </ToggleButtonGroup>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                          pt: 0,
                          pb: 2,
                          px: 2,
                        }}
                      >
                        {/* Attachment Content Container */}
                        <Box
                          sx={{
                            position: "relative",
                          }}
                        >
                          {/* List View */}
                          {attachmentView === "list" && (
                            <AttachmentListView
                              attachments={combinedAttachments}
                              isLoading={
                                isLoadingAttachment || combiningAttachments
                              }
                              onView={handleViewAttachment}
                              onEdit={(attachment) => {
                                if (
                                  attachment.fileType?.startsWith("image/") ||
                                  attachment.type === "screenshot"
                                ) {
                                  handleAnnotateAttachment(
                                    attachment.originalIndex,
                                  );
                                }
                              }}
                              onDelete={(attachment) => {
                                if (attachment.originalIndex >= 0) {
                                  handleRemoveAttachment(
                                    attachment.originalIndex,
                                  );
                                } else if (attachment.type === "screenshot") {
                                  handleRemoveScreenshot();
                                }
                              }}
                              anchorEl={anchorEl}
                              selectedRowForMenu={selectedRowForMenu}
                              onMenuOpen={handleMenuOpen}
                              onMenuClose={handleMenuClose}
                              onFileUpload={(files) => processFiles(files)}
                              isDropZone={
                                !isCurrentUserClient ||
                                bugDetail?.bugFor !== "BOARD"
                              }
                              onTranscribe={handleTranscribeVideo}
                              transcribingVideos={transcribingVideos}
                              transcriptionProgress={transcriptionProgress}
                            />
                          )}

                          {/* Grid View */}
                          {attachmentView === "grid" && (
                            <AttachmentGridView
                              attachments={combinedAttachments}
                              isLoading={
                                isLoadingAttachment || combiningAttachments
                              }
                              onView={handleViewAttachment}
                              onEdit={(attachment) => {
                                if (
                                  attachment.fileType?.startsWith("image/") ||
                                  attachment.type === "screenshot"
                                ) {
                                  handleAnnotateAttachment(
                                    attachment.originalIndex,
                                  );
                                }
                              }}
                              onDelete={(attachment) => {
                                if (attachment.originalIndex >= 0) {
                                  handleRemoveAttachment(
                                    attachment.originalIndex,
                                  );
                                } else if (attachment.type === "screenshot") {
                                  handleRemoveScreenshot();
                                }
                              }}
                              anchorEl={anchorEl}
                              selectedRowForMenu={selectedRowForMenu}
                              onMenuOpen={handleMenuOpen}
                              onMenuClose={handleMenuClose}
                              onFileUpload={(files) => processFiles(files)}
                              isDropZone={
                                !isCurrentUserClient ||
                                bugDetail?.bugFor !== "BOARD"
                              }
                              onTranscribe={handleTranscribeVideo}
                              transcribingVideos={transcribingVideos}
                              transcriptionProgress={transcriptionProgress}
                            />
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    {/* Time Log - Full Width */}
                    {bugDetailData?.subTasksCount === 0 &&
                      bugDetail?.type !== "EPIC" &&
                      !isCurrentUserClient && (
                        <Accordion
                          defaultExpanded={false}
                          sx={{
                            mb: 3,
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: "8px !important",
                            boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
                            "&:before": {
                              display: "none",
                            },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              minHeight: 48,
                              "& .MuiAccordionSummary-content": {
                                my: 1,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                pr: 2,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <AccessTime sx={{ fontSize: 18 }} />
                                {t("Time Log")}
                              </Typography>
                              {(isSavingTimeLog || isUpdatingWorkLog) && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <CircularProgress size={12} />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {t("Saving...")}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                            {/* Time Log Grid - matching tickets.html design */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(4, 1fr)",
                                gap: "10px",
                                mb: 2,
                              }}
                            >
                              {getCurrentWeekDates().map((date, index) => {
                                const dateKey = formatDateKey(date);
                                const currentLog = timeLogs[dateKey] || {
                                  hours: 0,
                                  date: dateKey,
                                };
                                const isToday =
                                  formatDateKey(new Date()) === dateKey;
                                const dayName = [
                                  "Mon",
                                  "Tue",
                                  "Wed",
                                  "Thu",
                                  "Fri",
                                  "Sat",
                                  "Sun",
                                ][index];

                                // Format date as "Jan 20", "Feb 2", etc.
                                const formattedDate = date.toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                  },
                                );

                                return (
                                  <Box
                                    key={dateKey}
                                    sx={{
                                      border: isToday
                                        ? `1px solid ${theme.palette.primary.main}`
                                        : `1px solid ${theme.palette.divider}`,
                                      borderRadius: "8px",
                                      overflow: "hidden",
                                      backgroundColor:
                                        theme.palette.background.paper,
                                      boxShadow: isToday
                                        ? `0 0 0 2px ${hexToRgba(theme.palette.primary.main, 0.1)}`
                                        : "none",
                                    }}
                                  >
                                    {/* Day Header */}
                                    <Box
                                      sx={{
                                        padding: "8px 10px",
                                        backgroundColor: isToday
                                          ? hexToRgba(
                                              theme.palette.primary.main,
                                              0.08,
                                            )
                                          : theme.palette.mode === "dark"
                                            ? theme.palette.background.default
                                            : "#f9fafb",
                                        fontSize: "11px",
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: "11px",
                                          color: isToday
                                            ? theme.palette.primary.main
                                            : "inherit",
                                        }}
                                      >
                                        {t(dayName)}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: "11px",
                                          color: "text.secondary",
                                        }}
                                      >
                                        {formattedDate}
                                      </Typography>
                                    </Box>

                                    {/* Day Input */}
                                    <FormControl fullWidth size="small">
                                      <Select
                                        value={currentLog.hours}
                                        onChange={(e) =>
                                          handleTimeLogChange(
                                            dateKey,
                                            Number(e.target.value),
                                          )
                                        }
                                        sx={{
                                          width: "100%",
                                          padding: "10px",
                                          border: "none",
                                          fontSize: "16px",
                                          fontWeight: 600,
                                          textAlign: "center",
                                          backgroundColor: "transparent",
                                          color: isToday
                                            ? theme.palette.primary.main
                                            : "inherit",
                                          "& .MuiOutlinedInput-notchedOutline":
                                            {
                                              border: "none",
                                            },
                                          "&:hover .MuiOutlinedInput-notchedOutline":
                                            {
                                              border: "none",
                                            },
                                          "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                            {
                                              border: "none",
                                            },
                                          "&:focus": {
                                            backgroundColor:
                                              theme.palette.mode === "dark"
                                                ? theme.palette.background
                                                    .default
                                                : "#f9fafb",
                                          },
                                          "& .MuiSelect-select": {
                                            padding: "10px",
                                            textAlign: "center",
                                            fontSize: "16px",
                                            fontWeight: 600,
                                            color: isToday
                                              ? theme.palette.primary.main
                                              : "inherit",
                                          },
                                        }}
                                        MenuProps={{
                                          PaperProps: {
                                            sx: {
                                              maxHeight: 200,
                                            },
                                          },
                                        }}
                                      >
                                        {timeLogOptions.map((hour) => (
                                          <MenuItem
                                            key={hour}
                                            value={hour}
                                            sx={{
                                              fontSize: "14px",
                                              justifyContent: "center",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {hour === 0 ? "--" : `${hour}h`}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                );
                              })}
                            </Box>

                            {/* Time Totals - matching tickets.html design */}
                            {(() => {
                              const currentWeekTotal = Object.values(timeLogs)
                                .filter((log) => {
                                  const weekDates = getCurrentWeekDates();
                                  return weekDates.some(
                                    (d) => formatDateKey(d) === log.date,
                                  );
                                })
                                .reduce((sum, log) => sum + log.hours, 0);

                              const otherWeeksTotal = getOtherWeeksTotal();

                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: "12px",
                                  }}
                                >
                                  {/* Total Current Week */}
                                  <Box
                                    sx={{
                                      flex: 1,
                                      padding: "12px",
                                      background: `linear-gradient(135deg, ${hexToRgba(theme.palette.primary.main, 0.08)} 0%, ${hexToRgba(theme.palette.primary.main, 0.12)} 100%)`,
                                      border: `1px solid ${hexToRgba(theme.palette.primary.main, 0.25)}`,
                                      borderRadius: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "11px",
                                        color: theme.palette.primary.dark,
                                        marginBottom: "4px",
                                      }}
                                    >
                                      {t("Total Current Week")}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: "18px",
                                        fontWeight: 700,
                                        color: theme.palette.primary.main,
                                      }}
                                    >
                                      {currentWeekTotal.toFixed(1)}h
                                    </Typography>
                                  </Box>

                                  {/* Total Other Weeks */}
                                  <Box
                                    sx={{
                                      flex: 1,
                                      padding: "12px",
                                      background: `linear-gradient(135deg, ${hexToRgba(theme.palette.primary.main, 0.08)} 0%, ${hexToRgba(theme.palette.primary.main, 0.12)} 100%)`,
                                      border: `1px solid ${hexToRgba(theme.palette.primary.main, 0.25)}`,
                                      borderRadius: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "11px",
                                        color: theme.palette.primary.dark,
                                        marginBottom: "4px",
                                      }}
                                    >
                                      {t("Total Other Weeks")}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: "18px",
                                        fontWeight: 700,
                                        color: theme.palette.primary.main,
                                      }}
                                    >
                                      {otherWeeksTotal.toFixed(1)}h
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            })()}
                          </AccordionDetails>
                        </Accordion>
                      )}

                    {/* Subtasks Section - Accordion */}
                    {!isCurrentUserClient && (
                      <Accordion
                        defaultExpanded={false}
                        sx={{
                          mt: 3,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: "8px !important",
                          boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
                          "&:before": {
                            display: "none",
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            minHeight: 48,
                            "& .MuiAccordionSummary-content": {
                              my: 1,
                            },
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <AccountTreeIcon sx={{ fontSize: 18 }} />
                            {t("Sub Tasks")} (
                            {bugDetailData?.subTasksCount || 0})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                          <SubtaskTab bugId={bugId} />
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Linked Tasks Section - Accordion */}
                    {!isCurrentUserClient && (
                      <Accordion
                        defaultExpanded={false}
                        sx={{
                          mt: 3,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: "8px !important",
                          boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
                          "&:before": {
                            display: "none",
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            minHeight: 48,
                            "& .MuiAccordionSummary-content": {
                              my: 1,
                            },
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <LinkIcon sx={{ fontSize: 18 }} />
                            {t("Linked Tasks")} (
                            {bugDetailData?.linkBugs?.length || 0})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                          <LinkedTask bugId={bugId} />
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Danger Zone Section */}
                    {DEFEKT_TRACKER.DELETE(myPermissions) &&
                      !isCurrentUserClient && (
                        <Accordion
                          sx={{
                            mt: 3,
                            mb: 3,
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(211, 47, 47, 0.08)"
                                : "rgba(211, 47, 47, 0.04)",
                            borderRadius: "8px !important",
                            border: "1px solid",
                            borderColor: "error.main",
                            boxShadow: "0 2px 8px rgba(211, 47, 47, 0.15)",
                            "&:before": {
                              display: "none",
                            },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={
                              <ExpandMoreIcon sx={{ color: "error.main" }} />
                            }
                            sx={{
                              "& .MuiAccordionSummary-content": {
                                margin: "12px 0",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <WarningAmberIcon
                                sx={{
                                  color: "error.main",
                                  fontSize: 24,
                                }}
                              />
                              <Box>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 700,
                                    color: "error.main",
                                  }}
                                >
                                  {t("Danger Zone")}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "error.dark",
                                    display: "block",
                                    mt: 0.25,
                                  }}
                                >
                                  {t("Irreversible and destructive actions")}
                                </Typography>
                              </Box>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails
                            sx={{
                              pt: 0,
                              pb: 2,
                              px: 2,
                            }}
                          >
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 1,
                                backgroundColor: "background.paper",
                                border: "1px solid",
                                borderColor: "error.light",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  gap: 2,
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      mb: 1,
                                      color: "text.primary",
                                    }}
                                  >
                                    {t("Delete this task permanently")}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      display: "block",
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {t(
                                      "Once you delete this task, there is no going back. All data including comments, attachments, and history will be permanently removed.",
                                    )}
                                  </Typography>
                                </Box>
                                <LoadingButton
                                  variant="contained"
                                  color="error"
                                  size="medium"
                                  loading={isDeleteLoading}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsDeleteReply(true);
                                  }}
                                  sx={{
                                    minWidth: 120,
                                    flexShrink: 0,
                                    fontWeight: 600,
                                    boxShadow:
                                      "0 2px 8px rgba(211, 47, 47, 0.25)",
                                    "&:hover": {
                                      boxShadow:
                                        "0 4px 12px rgba(211, 47, 47, 0.35)",
                                    },
                                  }}
                                >
                                  {t("Delete Task")}
                                </LoadingButton>
                              </Box>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}
                  </Grid2>

                  {/* Right Grid - 30% */}
                  <Grid2 size={4}>
                    {/* Task Information */}
                    <Box
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 2,
                        p: 1.5,
                        mb: 2,
                        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {/* <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          fontSize: "12px",
                          color: "text.primary",
                          mb: 1.2,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("Task Information")}
                      </Typography> */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {/* Compact Updated and Reported */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          {/* Updated */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <UpdateIcon
                              sx={{
                                fontSize: 13,
                                color: "text.secondary",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "text.secondary",
                              }}
                            >
                              {t("Updated")}:
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: "text.primary",
                                fontWeight: 500,
                              }}
                            >
                              {moment
                                .utc(bugDetail?.updatedAt)
                                .local()
                                .format(`DD MMM, YYYY HH:mm`)}
                            </Typography>
                          </Box>

                          {/* Reported */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <PersonIcon
                              sx={{
                                fontSize: 13,
                                color: "text.secondary",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "text.secondary",
                              }}
                            >
                              {t("Reported")}:
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: "text.primary",
                                fontWeight: 500,
                              }}
                            >
                              {moment
                                .utc(bugDetail?.createdAt)
                                .local()
                                .format(`DD MMM, YYYY`)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: "text.secondary",
                              }}
                            >
                              by {getCreatorName()}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {/* Status, Severity, Assignees, Tags */}
                    <Box
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {/* Single Column Layout for fields */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {/* Project */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              color: "text.secondary",
                              fontWeight: 500,
                              minWidth: "100px",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <FolderIcon sx={{ fontSize: 14 }} />
                            {t("Project")}
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select
                              value={extractId(bugDetail?.project) || ""}
                              onChange={handleProjectSelect}
                              size="small"
                              disabled={
                                bugDetail?.parentBug || isCurrentUserClient
                              }
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxWidth: "300px",
                                  },
                                },
                              }}
                              sx={{
                                fontSize: "13px",
                                backgroundColor: theme.palette.background.paper,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: theme.palette.divider,
                                },
                              }}
                            >
                              {projectlist?.map((option) => (
                                <MenuItem key={option._id} value={option._id}>
                                  {option.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>

                        {/* Jira Project - Only show if platform is JIRA */}
                        {/* {bugDetail?.platform === "JIRA" && !isCurrentUserClient && (
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "13px",
                                  color: "text.secondary",
                                  fontWeight: 500,
                                  minWidth: "100px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CloudIcon sx={{ fontSize: 14 }} />
                                {t("Jira Project")}
                              </Typography>
                              <FormControl fullWidth size="small">
                                <Select
                                  disabled
                                  value={
                                    bugDetailData?.jiraConfig?.projectKey || ""
                                  }
                                  onChange={handleJiraProjectSelect}
                                  size="small"
                                  sx={{
                                    fontSize: "13px",
                                    backgroundColor:
                                      theme.palette.background.paper,
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: theme.palette.divider,
                                    },
                                  }}
                                >
                                  <MenuItem disabled value="">
                                    {isLoadingProjects
                                      ? t("Checking JIRA configuration...")
                                      : isJiraConfigured
                                        ? t("Select Project")
                                        : t("Select Project")}
                                  </MenuItem>
                                  {isLoadingProjects ? (
                                    <MenuItem disabled>
                                      {t("Loading projects")}
                                    </MenuItem>
                                  ) : (
                                    Array.isArray(jiraprojectList) &&
                                    jiraprojectList?.map((project) => (
                                      <MenuItem
                                        key={project.key}
                                        value={project.key}
                                      >
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                          justifyContent="space-between"
                                          width="100%"
                                        >
                                          <Typography sx={{ fontSize: "13px" }}>
                                            {project?.projectData?.name ||
                                              project?.projectName}{" "}
                                            (
                                            {project?.projectData?.key ||
                                              project?.key}
                                            )
                                          </Typography>
                                        </Box>
                                      </MenuItem>
                                    ))
                                  )}
                                </Select>
                              </FormControl>
                            </Box>

                            {!isJiraConfigured && !isLoadingProjects && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{
                                  display: "block",
                                  mt: 0.5,
                                  fontSize: "11px",
                                  ml: "92px",
                                }}
                              >
                                {t(
                                  "JIRA integration is not configured or does not exist any project. Click",
                                )}{" "}
                                <span
                                  onClick={handleClickHere}
                                  style={{
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {t("here")}
                                </span>{" "}
                                {t("to integrate.")}
                              </Typography>
                            )}
                          </Box>
                        )} */}

                        {/* Status */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              color: "text.secondary",
                              fontWeight: 500,
                              minWidth: "100px",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <CheckCircleIcon sx={{ fontSize: 14 }} />
                            {t("Status")}
                          </Typography>
                          <FormControl
                            fullWidth
                            size="small"
                            error={!!validationErrors.bugStatus}
                          >
                            <Select
                              size="small"
                              value={bugDetail?.bugStatus || ""}
                              disabled={isCurrentUserClient}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxWidth: "300px",
                                  },
                                },
                              }}
                              sx={{
                                fontSize: "13px",
                                backgroundColor: theme.palette.background.paper,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: theme.palette.divider,
                                },
                              }}
                              onChange={(e) => {
                                setBugDetail((prev) => ({
                                  ...prev,
                                  bugStatus: e.target.value,
                                }));
                                if (validationErrors.bugStatus) {
                                  setValidationErrors((prev) => ({
                                    ...prev,
                                    bugStatus: null,
                                  }));
                                }
                              }}
                              error={!!validationErrors.bugStatus}
                            >
                              <MenuItem disabled value="">
                                {t("Select Status")}
                              </MenuItem>
                              {isLoadingStatusLabel ? (
                                <MenuItem disabled>
                                  {t("Loading Statuses")}
                                </MenuItem>
                              ) : (
                                availableStatus?.map((option) => (
                                  <MenuItem key={option._id} value={option._id}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 12,
                                          height: 12,
                                          backgroundColor:
                                            option.color ||
                                            option.bgColor ||
                                            "#f44336",
                                          borderRadius: "2px",
                                        }}
                                      />
                                      {option.status}
                                    </Box>
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                        </Box>

                        {/* Severity */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              color: "text.secondary",
                              fontWeight: 500,
                              minWidth: "100px",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <FlagIcon sx={{ fontSize: 14 }} />
                            {t("Severity")}
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select
                              value={bugDetail?.severity || ""}
                              onChange={(e) =>
                                setBugDetail((prev) => ({
                                  ...prev,
                                  severity: e.target.value,
                                }))
                              }
                              disabled={
                                (bugDetailData?.platform === "JIRA" &&
                                  !getProjectIdForDetails()) ||
                                !bugDetailData?.platform ||
                                isCurrentUserClient
                              }
                              size="small"
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxWidth: "300px",
                                  },
                                },
                              }}
                              sx={{
                                fontSize: "13px",
                                backgroundColor: theme.palette.background.paper,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: theme.palette.divider,
                                },
                              }}
                              renderValue={(selected) => {
                                const selectedOption = severityOptions.find(
                                  (opt) =>
                                    opt.value.toLowerCase() ===
                                    selected?.toLowerCase(),
                                );
                                if (!selectedOption) return selected || "";
                                return (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <Box sx={{ width: 16, height: 16 }}>
                                      {selectedOption.icon}
                                    </Box>
                                    {selectedOption.label}
                                  </Box>
                                );
                              }}
                            >
                              {severityOptions.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <Box sx={{ width: 16, height: 16 }}>
                                      {option.icon}
                                    </Box>
                                    {option.label}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>

                        {/* Assignees */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              color: "text.secondary",
                              fontWeight: 500,
                              minWidth: "100px",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <PeopleIcon sx={{ fontSize: 14 }} />
                            {bugDetail?.parentBug
                              ? t("Assignee")
                              : t("Assignees")}
                          </Typography>
                          {bugDetail?.parentBug ? (
                            // Single select for subtasks
                            <Autocomplete
                              sx={{
                                flex: 1,
                                maxWidth: "calc(100% - 120px)",
                                "& .MuiAutocomplete-inputRoot": {
                                  flexWrap: "nowrap",
                                  maxWidth: "100%",
                                },
                              }}
                              ListboxProps={{
                                sx: {
                                  maxWidth: "400px",
                                },
                              }}
                              options={availableUsers || []}
                              getOptionLabel={(option) => option.fullName}
                              disabled={
                                (bugDetailData?.platform === "JIRA" &&
                                  !getProjectIdForDetails()) ||
                                isCurrentUserClient
                              }
                              value={
                                availableUsers?.find((user) =>
                                  (Array.isArray(bugDetail?.assignTo)
                                    ? bugDetail.assignTo
                                    : []
                                  ).includes(user._id),
                                ) || null
                              }
                              size="small"
                              onChange={(_, newValue) => {
                                setBugDetail((prev) => ({
                                  ...prev,
                                  assignTo: newValue ? [newValue._id] : [],
                                }));
                              }}
                              renderOption={(props, option) => (
                                <li {...props}>
                                  <Avatar
                                    sx={{
                                      bgcolor: theme.palette.primary.main,
                                      color: "#ffffff !important",
                                      width: 24,
                                      height: 24,
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      marginRight: 1,
                                    }}
                                  >
                                    {option?.fullName
                                      ?.split(" ")
                                      .map((n) => n.charAt(0))
                                      .join("")
                                      .toUpperCase()
                                      .slice(0, 2)}
                                  </Avatar>
                                  <Typography variant="body2">
                                    {option.fullName}
                                  </Typography>
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder={t("Select assignee...")}
                                />
                              )}
                            />
                          ) : (
                            // Multiple select for main tasks
                            <Autocomplete
                              sx={{
                                flex: 1,
                                maxWidth: "calc(100% - 120px)",
                                "& .MuiAutocomplete-inputRoot": {
                                  flexWrap: "nowrap",
                                  maxWidth: "100%",
                                },
                                "& .MuiAutocomplete-tag": {
                                  maxWidth: "150px",
                                },
                              }}
                              ListboxProps={{
                                sx: {
                                  maxWidth: "400px",
                                },
                              }}
                              multiple
                              limitTags={2}
                              options={availableUsers || []}
                              getOptionLabel={(option) => option.fullName}
                              disableCloseOnSelect
                              disabled={
                                (bugDetailData?.platform === "JIRA" &&
                                  !getProjectIdForDetails()) ||
                                isCurrentUserClient
                              }
                              value={
                                availableUsers?.filter((user) =>
                                  (Array.isArray(bugDetail?.assignTo)
                                    ? bugDetail.assignTo
                                    : []
                                  ).includes(user._id),
                                ) || []
                              }
                              size="small"
                              onChange={(_, newValues) => {
                                setBugDetail((prev) => ({
                                  ...prev,
                                  assignTo: newValues.map((user) => user._id),
                                }));
                              }}
                              renderTags={(tagValues, getTagProps) => {
                                const visibleUsers =
                                  tagValues?.slice(0, 2) || [];
                                const remainingCount = tagValues?.length - 2;

                                return (
                                  <>
                                    {visibleUsers.map((user, index) => (
                                      <Chip
                                        {...getTagProps({ index })}
                                        key={user._id}
                                        avatar={
                                          <Avatar
                                            sx={{
                                              bgcolor:
                                                theme.palette.primary.main,
                                              color: "#ffffff !important",
                                              width: 24,
                                              height: 24,
                                              fontSize: "10px",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {user?.fullName
                                              ?.split(" ")
                                              .map((n) => n.charAt(0))
                                              .join("")
                                              .toUpperCase()
                                              .slice(0, 2)}
                                          </Avatar>
                                        }
                                        label={user.fullName}
                                        size="small"
                                        sx={{
                                          maxWidth: "150px",
                                          "& .MuiChip-label": {
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          },
                                        }}
                                      />
                                    ))}
                                    {remainingCount > 0 && (
                                      <Tooltip
                                        title={
                                          <Box>
                                            {tagValues?.slice(2).map((user) => (
                                              <Box
                                                key={user._id}
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                  mb: 0.5,
                                                }}
                                              >
                                                <Avatar
                                                  sx={{
                                                    bgcolor:
                                                      theme.palette.primary
                                                        .main,
                                                    width: 20,
                                                    height: 20,
                                                    fontSize: "9px",
                                                  }}
                                                >
                                                  {user?.fullName
                                                    ?.split(" ")
                                                    .map((n) => n.charAt(0))
                                                    .join("")
                                                    .toUpperCase()
                                                    .slice(0, 2)}
                                                </Avatar>
                                                {user.fullName}
                                              </Box>
                                            ))}
                                          </Box>
                                        }
                                      >
                                        <Chip
                                          size="small"
                                          label={`+${remainingCount}`}
                                          sx={{
                                            height: "24px",
                                            cursor: "pointer",
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </>
                                );
                              }}
                              renderOption={(props, option) => (
                                <li {...props}>
                                  <Avatar
                                    sx={{
                                      bgcolor: theme.palette.primary.main,
                                      color: "#ffffff !important",
                                      width: 24,
                                      height: 24,
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      marginRight: 1,
                                    }}
                                  >
                                    {option?.fullName
                                      ?.split(" ")
                                      .map((n) => n.charAt(0))
                                      .join("")
                                      .toUpperCase()
                                      .slice(0, 2)}
                                  </Avatar>
                                  <Typography variant="body2">
                                    {option.fullName}
                                  </Typography>
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder={t("Select assignees...")}
                                />
                              )}
                            />
                          )}
                        </Box>

                        {/* Tags */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              color: "text.secondary",
                              fontWeight: 500,
                              minWidth: "100px",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: "9px",
                            }}
                          >
                            <LabelIcon sx={{ fontSize: 14 }} />
                            {t("Tags")}
                          </Typography>
                          <Autocomplete
                            sx={{
                              flex: 1,
                              maxWidth: "calc(100% - 120px)",
                              "& .MuiAutocomplete-inputRoot": {
                                flexWrap: "nowrap",
                                maxWidth: "100%",
                                alignItems: "center",
                              },
                              "& .MuiAutocomplete-tag": {
                                maxWidth: "150px",
                                margin: "2px",
                              },
                            }}
                            ListboxProps={{
                              sx: {
                                maxWidth: "400px",
                              },
                            }}
                            multiple
                            freeSolo
                            limitTags={2}
                            options={[
                              ...availableTags,
                              tagInput &&
                              !availableTags?.some(
                                (tag) => tag.tagName === tagInput,
                              )
                                ? { tagName: tagInput }
                                : null,
                            ]?.filter(Boolean)}
                            getOptionLabel={(option) => option.tagName}
                            disabled={
                              !getProjectIdForTags() || isCurrentUserClient
                            }
                            disableCloseOnSelect
                            value={availableTags.filter((tag) =>
                              (Array.isArray(bugDetail?.tags)
                                ? bugDetail.tags
                                : []
                              ).includes(tag.tagName),
                            )}
                            size="small"
                            onChange={async (_, newValues) => {
                              if (
                                tagInput &&
                                !newValues.some(
                                  (tag) => tag.tagName === tagInput,
                                )
                              ) {
                                return;
                              }

                              const newTag = newValues.find(
                                (tag) =>
                                  !availableTags?.some(
                                    (t) => t.tagName === tag.tagName,
                                  ),
                              );

                              if (newTag) {
                                const payload = {
                                  project: getProjectIdForTags(),
                                  tagName: newTag.tagName,
                                };
                                setIsCreatingTag(true);
                                try {
                                  const res = await createTag(payload).unwrap();
                                  if (res) {
                                    await refetchTags();
                                  }
                                } catch (error) {
                                  console.error("Error creating tag:", error);
                                  toast.error(t("Failed to create tag"));
                                } finally {
                                  setIsCreatingTag(false);
                                }
                              }

                              setBugDetail((prev) => ({
                                ...prev,
                                tags: newValues.map((tag) => tag.tagName),
                              }));
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                              }
                            }}
                            onInputChange={(_event, newInputValue) => {
                              setTagInput(newInputValue);
                            }}
                            renderTags={(tagValues, getTagProps) => {
                              const visibleTags = tagValues?.slice(0, 2) || [];
                              const remainingCount = tagValues?.length - 2;

                              return (
                                <>
                                  {visibleTags.map((option, index) => (
                                    <StyledChip
                                      {...getTagProps({ index })}
                                      key={option._id || option.tagName}
                                      label={option.tagName}
                                      size="small"
                                      icon={
                                        <LocalOfferOutlinedIcon
                                          sx={{
                                            color: theme.palette.primary.main,
                                            fontSize: "14px",
                                          }}
                                        />
                                      }
                                    />
                                  ))}
                                  {remainingCount > 0 && (
                                    <Tooltip
                                      title={
                                        <Box>
                                          {tagValues?.slice(2).map((tag) => (
                                            <Box key={tag._id || tag.tagName}>
                                              {tag.tagName}
                                            </Box>
                                          ))}
                                        </Box>
                                      }
                                    >
                                      <Chip
                                        size="small"
                                        label={`+${remainingCount}`}
                                        sx={{
                                          height: "24px",
                                          cursor: "pointer",
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </>
                              );
                            }}
                            renderOption={(props, option, { selected }) => {
                              const isCustomTag =
                                option.tagName === tagInput && tagInput !== "";
                              return (
                                <li {...props}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: "100%",
                                    }}
                                  >
                                    {!isCustomTag ? (
                                      <LocalOfferOutlinedIcon
                                        sx={{
                                          marginRight: 1,
                                          fontSize: "15px",
                                        }}
                                      />
                                    ) : null}
                                    <Typography
                                      sx={{
                                        flexGrow: 1,
                                        fontSize: "13px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      {isCustomTag ? (
                                        <>
                                          <AddCircleOutlineOutlinedIcon
                                            sx={{
                                              fontSize: "18px",
                                              mr: 1,
                                              color: "#63BA3C",
                                            }}
                                          />
                                          {tagInput}{" "}
                                        </>
                                      ) : (
                                        option.tagName
                                      )}
                                    </Typography>
                                    {selected && (
                                      <CheckIcon
                                        sx={{
                                          color: theme.palette.primary.main,
                                          ml: 1,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <StyledTextField
                                {...params}
                                placeholder={t("Add Tags")}
                                variant="outlined"
                                size="small"
                                sx={{ marginBottom: 0 }}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {isCreatingTag ? (
                                        <CircularProgress
                                          color="inherit"
                                          size={20}
                                        />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        </Box>

                        {/* Page URL */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              color: "text.secondary",
                              fontWeight: 500,
                              minWidth: "100px",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <LanguageIcon sx={{ fontSize: 14 }} />
                            {t("Page URL")}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flex: 1,
                              minWidth: 0,
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? theme.palette.background.paper
                                  : "#f8fafc",
                              borderRadius: 1,
                              px: 1.5,
                              py: 0.75,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "13px",
                                color: bugDetail?.pageUrl
                                  ? "text.primary"
                                  : "text.secondary",
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {bugDetail?.pageUrl || t("No URL")}
                            </Typography>
                            {bugDetail?.pageUrl && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  window.open(bugDetail.pageUrl, "_blank")
                                }
                                sx={{
                                  padding: 0.5,
                                  "&:hover": {
                                    backgroundColor: "action.hover",
                                  },
                                }}
                              >
                                <OpenInNewIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                        {/* Required Skillset */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              color: "text.secondary",
                              fontWeight: 500,
                              minWidth: "100px",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <SchoolIcon sx={{ fontSize: 14 }} />
                            {t("Skillset")}
                          </Typography>
                          <Autocomplete
                            sx={{ flex: 1 }}
                            options={skillSetOptions}
                            value={bugDetail?.requiredSkillSet || null}
                            onChange={(_event, newValue) => {
                              setBugDetail((prev) => ({
                                ...prev,
                                requiredSkillSet: newValue || "",
                              }));
                            }}
                            disabled={
                              !bugDetail?.project || isCurrentUserClient
                            }
                            size="small"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={t("Select skills")}
                                variant="outlined"
                                size="small"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    fontSize: "13px",
                                    backgroundColor:
                                      theme.palette.background.paper,
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: theme.palette.divider,
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        </Box>

                        {/* Issue Type */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              color: "text.secondary",
                              fontWeight: 500,
                              minWidth: "100px",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <CategoryIcon sx={{ fontSize: 14 }} />
                            {t("Issue Type")}
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select
                              value={bugDetail?.type || ""}
                              onChange={(e) => {
                                setBugDetail((prev) => ({
                                  ...prev,
                                  type: e.target.value,
                                }));
                                setStoryError("");
                              }}
                              disabled={isCurrentUserClient}
                              size="small"
                              renderValue={(selected) => {
                                const selectedOption = issueTypeItems.find(
                                  (opt: any) =>
                                    (opt?.value || opt?.code) === selected,
                                );
                                return (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    {selectedOption?.icon ||
                                      getIssueTypeIcon(selected as string)}
                                    {selectedOption?.label || selected}
                                  </Box>
                                );
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxWidth: "300px",
                                  },
                                },
                              }}
                              sx={{
                                fontSize: "13px",
                                backgroundColor: theme.palette.background.paper,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: theme.palette.divider,
                                },
                              }}
                            >
                              {issueTypeItems.map((option) => {
                                const isEpicOption =
                                  option?.value === "EPIC" ||
                                  option?.code === "EPIC";
                                const shouldDisable =
                                  bugDetail?.parentBug !== null && isEpicOption;

                                return (
                                  <MenuItem
                                    key={option?.value || option?.code}
                                    value={option?.value || option?.code}
                                    disabled={shouldDisable}
                                  >
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                      width="100%"
                                    >
                                      {option.icon ||
                                        getIssueTypeIcon(
                                          option?.value || option?.code,
                                        )}
                                      {option.label}
                                    </Box>
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </Box>

                        {/* Link to Story */}
                        {(bugDetail?.type === "BUG" ||
                          bugDetail?.type === "DEFECT" ||
                          bugDetail?.type === "SUGGESTIONS") &&
                          !isCurrentUserClient && (
                            <Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  mb: 1,
                                }}
                              >
                                <BookmarkIcon
                                  sx={{ fontSize: 14, color: "text.secondary" }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "13px",
                                    color: "text.secondary",
                                    fontWeight: 500,
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {t("Link to Story")}
                                  {(
                                    projectDetails?.data?.decryptedData
                                      ?.storyRequiredTypes || []
                                  ).includes(bugDetail?.type) && (
                                    <Box
                                      component="span"
                                      sx={{
                                        color: theme.palette.error.main,
                                        ml: 0.5,
                                      }}
                                    >
                                      *
                                    </Box>
                                  )}
                                </Typography>
                                <Tooltip
                                  title={t(
                                    "You can make this field required or optional from Project Settings",
                                  )}
                                  arrow
                                  placement="top"
                                >
                                  <InfoOutlinedIcon
                                    sx={{
                                      fontSize: 16,
                                      color: theme.palette.text.secondary,
                                      cursor: "help",
                                    }}
                                  />
                                </Tooltip>
                                {!(
                                  projectDetails?.data?.decryptedData
                                    ?.storyRequiredTypes || []
                                ).includes(bugDetail?.type) && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: theme.palette.text.secondary,
                                      fontSize: "11px",
                                    }}
                                  >
                                    ({t("Optional")})
                                  </Typography>
                                )}
                              </Box>

                              <Autocomplete
                                fullWidth
                                size="small"
                                options={stories}
                                loading={isStoriesFetching}
                                getOptionLabel={(option) => option.summary}
                                value={
                                  stories.find((s) => s.id === selectedStory) ||
                                  null
                                }
                                onChange={(_event, newValue) =>
                                  handleStorySelect(
                                    newValue ? newValue.id : null,
                                  )
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder={t("Search or select a story")}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        fontSize: "13px",
                                        backgroundColor:
                                          theme.palette.background.paper,
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: theme.palette.divider,
                                        },
                                      },
                                    }}
                                    InputProps={{
                                      ...params.InputProps,
                                      startAdornment: (
                                        <>
                                          {selectedStory ? (
                                            <Box
                                              sx={{
                                                ml: 1,
                                                mr: 0.5,
                                                width: 18,
                                                height: 18,
                                                borderRadius: "3px",
                                                backgroundColor: "#4caf50",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <BookmarkIcon
                                                sx={{
                                                  fontSize: 12,
                                                  color: "#ffffff",
                                                }}
                                              />
                                            </Box>
                                          ) : (
                                            <SearchIcon
                                              sx={{
                                                ml: 1,
                                                mr: 0.5,
                                                color:
                                                  theme.palette.text.secondary,
                                                fontSize: 20,
                                              }}
                                            />
                                          )}
                                          {params.InputProps.startAdornment}
                                        </>
                                      ),
                                      endAdornment: (
                                        <>
                                          {isStoriesFetching ? (
                                            <CircularProgress
                                              color="inherit"
                                              size={20}
                                            />
                                          ) : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                  />
                                )}
                                renderOption={(props, option) => (
                                  <Box
                                    component="li"
                                    {...props}
                                    key={option.id}
                                  >
                                    <Box sx={{ width: "100%" }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1.5,
                                          mb: 0.5,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: "3px",
                                            backgroundColor: "#4caf50",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                          }}
                                        >
                                          <BookmarkIcon
                                            sx={{
                                              fontSize: 12,
                                              color: "#ffffff",
                                            }}
                                          />
                                        </Box>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            flex: 1,
                                            fontWeight: 500,
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {option.summary}
                                        </Typography>
                                      </Box>
                                      {option.severity && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            ml: 3.5,
                                            mt: 0.5,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 14,
                                              height: 14,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              "& svg": {
                                                width: "100%",
                                                height: "100%",
                                              },
                                            }}
                                          >
                                            {getSeverityIcon(option.severity)}
                                          </Box>
                                          <Typography
                                            variant="caption"
                                            sx={{ fontSize: "0.7rem" }}
                                          >
                                            {option.severity}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                )}
                                noOptionsText={
                                  <Box sx={{ py: 2, textAlign: "center" }}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {t("No stories found")}
                                    </Typography>
                                  </Box>
                                }
                                loadingText={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: 1,
                                      py: 2,
                                    }}
                                  >
                                    <CircularProgress size={20} />
                                    <Typography variant="body2">
                                      {t("Loading stories...")}
                                    </Typography>
                                  </Box>
                                }
                              />

                              {selectedStory && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    p: 0.5,
                                    borderRadius: 1.5,
                                    border: `1px solid ${hexToRgba(
                                      theme.palette.primary.main,
                                      0.1,
                                    )}`,
                                    backgroundColor: hexToRgba(
                                      theme.palette.primary.main,
                                      0.1,
                                    ),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                      flex: 1,
                                    }}
                                  >
                                    <CheckIcon
                                      sx={{
                                        color: theme.palette.primary.main,
                                        fontSize: 20,
                                      }}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          color: theme.palette.primary.main,
                                          mb: 0.5,
                                          fontSize: "0.7rem",
                                        }}
                                      >
                                        {t("Linked to Story")}
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 14,
                                            height: 14,
                                            borderRadius: "3px",
                                            backgroundColor: "#63BA3C",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                          }}
                                        >
                                          <BookmarkIcon
                                            sx={{
                                              fontSize: 8,
                                              color: "#ffffff",
                                            }}
                                          />
                                        </Box>
                                        <Typography
                                          variant="body2"
                                          sx={{ fontSize: "0.7rem" }}
                                        >
                                          {stories.find(
                                            (s) => s.id === selectedStory,
                                          )?.summary || t("Story")}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setSelectedStory(null);
                                      setStoryError("");
                                    }}
                                    sx={{
                                      ml: 1,
                                      color: theme.palette.primary.main,
                                    }}
                                  >
                                    {t("Remove")}
                                  </Button>
                                </Box>
                              )}

                              {storyError && (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{ mt: 0.5, display: "block" }}
                                >
                                  {storyError}
                                </Typography>
                              )}
                            </Box>
                          )}

                        {/* Estimation */}
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "12px",
                              color: "text.secondary",
                              fontWeight: 500,
                              mb: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <TimerIcon sx={{ fontSize: 14 }} />
                            {t("Estimation")}
                          </Typography>
                          <Box
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            <TextField
                              size="small"
                              label={t("Days")}
                              type="number"
                              disabled={isCurrentUserClient}
                              value={bugDetail.estimationDays || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (
                                  value === "" ||
                                  (parseInt(value) >= 0 &&
                                    !isNaN(parseInt(value)))
                                ) {
                                  handleEstimationDaysChange(value);
                                }
                              }}
                              inputProps={{
                                min: 0,
                                step: 1,
                                onKeyDown: (e) => {
                                  if (
                                    ["-", "e", "E", "+", "."].includes(e.key)
                                  ) {
                                    e.preventDefault();
                                  }
                                },
                              }}
                              sx={{
                                minWidth: 70,
                                flex: 1,
                                "& .MuiOutlinedInput-root": {
                                  fontSize: "13px",
                                },
                              }}
                            />
                            <TextField
                              size="small"
                              label={t("Hours")}
                              type="number"
                              value={bugDetail.estimationHours || ""}
                              disabled={isCurrentUserClient}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (
                                  value === "" ||
                                  (parseInt(value) >= 0 &&
                                    parseInt(value) <= 23 &&
                                    !isNaN(parseInt(value)))
                                ) {
                                  handleEstimationHoursChange(value);
                                }
                              }}
                              inputProps={{
                                min: 0,
                                max: 23,
                                step: 1,
                                onKeyDown: (e) => {
                                  if (
                                    ["-", "e", "E", "+", "."].includes(e.key)
                                  ) {
                                    e.preventDefault();
                                  }
                                },
                              }}
                              sx={{
                                minWidth: 70,
                                flex: 1,
                                "& .MuiOutlinedInput-root": {
                                  fontSize: "13px",
                                },
                              }}
                            />
                            <FormControl
                              size="small"
                              sx={{ minWidth: 80, flex: 1 }}
                            >
                              <InputLabel
                                sx={{
                                  fontSize: "13px",
                                  transform: "translate(14px, 9px) scale(1)",
                                  "&.Mui-focused, &.MuiFormLabel-filled": {
                                    transform:
                                      "translate(14px, -9px) scale(0.75)",
                                  },
                                }}
                              >
                                {t("Minutes")}
                              </InputLabel>
                              <Select
                                value={bugDetail.estimationMinutes || ""}
                                disabled={isCurrentUserClient}
                                onChange={(e) =>
                                  handleEstimationMinutesChange(
                                    e.target.value as string,
                                  )
                                }
                                label={t("Minutes")}
                                sx={{
                                  fontSize: "13px",
                                }}
                              >
                                {minuteOptions.map((option) => (
                                  <MenuItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                          {bugDetail.estimation && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{
                                mt: 0.5,
                                display: "block",
                                fontSize: "11px",
                              }}
                            >
                              {t("Total")}: {bugDetail.estimation}
                            </Typography>
                          )}
                        </Box>

                        {/* Sprint Selection - Only for SCRUM boards */}
                        {bugDetail?.boardType === "SCRUM" && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "12px",
                                color: "text.secondary",
                                fontWeight: 500,
                                minWidth: "80px",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <DirectionsRunIcon sx={{ fontSize: 14 }} />
                              {t("Sprint")}
                            </Typography>
                            <FormControl fullWidth size="small">
                              <Select
                                value={
                                  bugDetail?.currentSprint
                                    ? typeof bugDetail.currentSprint ===
                                      "object"
                                      ? bugDetail.currentSprint._id
                                      : bugDetail.currentSprint
                                    : ""
                                }
                                onChange={(e) => {
                                  setBugDetail((prev) => ({
                                    ...prev,
                                    currentSprint: e.target.value || null,
                                  }));
                                }}
                                disabled={isCurrentUserClient}
                                size="small"
                                sx={{
                                  fontSize: "13px",
                                  backgroundColor:
                                    theme.palette.background.paper,
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: theme.palette.divider,
                                  },
                                }}
                              >
                                <MenuItem value="">{t("No Sprint")}</MenuItem>
                                {(sprints || []).map((sprint) => (
                                  <MenuItem key={sprint._id} value={sprint._id}>
                                    {sprint.name}
                                    {sprint.status === "ACTIVE" && (
                                      <Chip
                                        label={t("Active")}
                                        size="small"
                                        color="primary"
                                        sx={{
                                          ml: 1,
                                          height: 16,
                                          fontSize: "10px",
                                        }}
                                      />
                                    )}
                                    {sprint.status === "DRAFT" && (
                                      <Chip
                                        label={t("Draft")}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          ml: 1,
                                          height: 16,
                                          fontSize: "10px",
                                        }}
                                      />
                                    )}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        )}

                        {/* Parent Epic - Only if type is NOT EPIC */}
                        {bugDetail?.type !== "EPIC" &&
                          bugDetail?.boardType === "SCRUM" && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "12px",
                                  color: "text.secondary",
                                  fontWeight: 500,
                                  minWidth: "80px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <AccountTreeIcon sx={{ fontSize: 14 }} />
                                {t("Parent Epic")}
                              </Typography>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={selectedEpic || "None"}
                                  onChange={(e) => {
                                    setSelectedEpic(e.target.value);
                                  }}
                                  size="small"
                                  sx={{
                                    fontSize: "13px",
                                    backgroundColor:
                                      theme.palette.background.paper,
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: theme.palette.divider,
                                    },
                                  }}
                                >
                                  <MenuItem value="None">
                                    <em>{t("None")}</em>
                                  </MenuItem>
                                  {(epics || []).map((epic) => (
                                    <MenuItem key={epic._id} value={epic._id}>
                                      {epic.summary}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          )}

                        {/* Set Recurring */}
                        {!isSniffer &&
                          bugDetail?.type !== "EPIC" &&
                          !isCurrentUserClient && (
                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={isRecurring}
                                    onChange={handleRecurringToggle}
                                    name="setRecurring"
                                    color="primary"
                                    size="small"
                                  />
                                }
                                label={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Typography variant="body2">
                                        {t("Set Recurring")}
                                      </Typography>
                                      {isRecurring && (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          onClick={handleRecurringConfigClick}
                                          startIcon={<SettingsIcon />}
                                          sx={{
                                            fontSize: "0.75rem",
                                            minWidth: "auto",
                                            px: 1,
                                            py: 0.5,
                                          }}
                                        >
                                          {t("Configure")}
                                        </Button>
                                      )}
                                    </Box>
                                    {isRecurring && (
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                      >
                                        {getRecurringDisplayText()}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </FormGroup>
                          )}
                      </Box>
                    </Box>

                    {/* Status Journey & Device */}
                    {(bugDetail?.bugJourney?.length > 0 ||
                      bugDetail?.deviceDetails) && (
                      <Box
                        sx={{
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: 2,
                          p: 2,
                          mb: 2,
                          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 3,
                            flexWrap: "nowrap",
                          }}
                        >
                          {/* Status Journey */}
                          {bugDetail?.bugJourney?.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minWidth: 0,
                                flexShrink: 0,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("Status Journey")}:
                              </Typography>
                              <BugJourneyTimelineDialog
                                bugJourney={bugDetail.bugJourney}
                                createdAt={bugDetail.createdAt}
                                updatedAt={bugDetail.updatedAt}
                              />
                            </Box>
                          )}

                          {/* Device */}
                          {bugDetail?.deviceDetails && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minWidth: 0,
                                maxWidth: "400px",
                              }}
                            >
                              <DevicesIcon
                                sx={{
                                  fontSize: 14,
                                  color: "text.secondary",
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  flexShrink: 0,
                                }}
                              >
                                {t("Device")}:
                              </Typography>
                              {parseDeviceDetails(bugDetail.deviceDetails) ? (
                                <Link
                                  component="button"
                                  variant="body2"
                                  onClick={handleDeviceDetailsClick}
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    textDecoration: "none",
                                    color: theme.palette.primary.main,
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    minWidth: 0,
                                    "&:hover": {
                                      textDecoration: "underline",
                                    },
                                  }}
                                >
                                  {getDeviceName(bugDetail.deviceDetails)}
                                </Link>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    color: "text.primary",
                                    fontWeight: 500,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    minWidth: 0,
                                  }}
                                >
                                  {getDeviceName(bugDetail.deviceDetails)}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Comments Section */}
                    <Box
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 2,
                        p: 2,
                        boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
                      }}
                    >
                      <Box>
                        <Comments
                          users={users}
                          taskId={bugId}
                          bugDetailData={bugDetailData}
                        />
                      </Box>
                    </Box>
                  </Grid2>
                </Grid2>
              </TabPanel>

              {/* Diagnostics Tab - VideoPreviewPanel with full media view */}
              <TabPanel value="2" sx={{ p: 0 }}>
                <Box sx={{ height: "calc(100vh - 120px)", minHeight: "500px" }}>
                  <VideoPreviewPanel
                    videoUrl={videoUrl || ""}
                    networkLogs={bugDetail?.logs?.network || []}
                    consoleLogs={bugDetail?.logs?.console || []}
                    actionLogs={bugDetail?.logs?.actions || []}
                    onClose={() => {}}
                    isOpen={true}
                    bugId={bugId}
                    users={users}
                    isClosedIconVisible={true}
                    bugDetailData={bugDetailData}
                    systemInfo={systemInfo}
                    images={combinedAttachments
                      .filter((att) => att.fileType?.startsWith("image/"))
                      .map((att) => ({
                        url: att.url,
                        label: att.name,
                        type: "attachment" as const,
                      }))}
                    reactFlow={reactFlow}
                  />
                </Box>
              </TabPanel>

              {/* Automation Tab - UI Session runner */}
              <TabPanel value="3" sx={{ p: 0 }}>
                <UISessionTab testScenario={bugDetail?.testScenario} />
              </TabPanel>

              {/* Customer Panel Tab */}
              <TabPanel value="4" sx={{ p: 0 }}>
                <CustomerPanel bugId={bugId} bugDetailData={bugDetailData} />
              </TabPanel>

              {/* Old Subtask Tab - Now merged into Overview */}
              {false && (
                <TabPanel value="5">
                  <SubtaskTab bugId={bugId} />
                </TabPanel>
              )}
              <TabPanel value="6">
                <BugAnalysis bugId={bugId} bugDetailData={bugDetailData} />
              </TabPanel>

              {/* Old Logs Tab - Now part of Diagnostics via VideoPreviewPanel */}
              {false && (
                <TabPanel value="6">
                  <LogsTab
                    consoleLogs={bugDetail?.logs?.console || []}
                    networkLogs={bugDetail?.logs?.network || []}
                    systemInfo={systemInfo}
                    actionLogs={bugDetail?.logs?.actions || []}
                  />
                </TabPanel>
              )}
            </TabContext>
          )}
        </Box>
      </Drawer>

      {/* Device Details Modal */}
      <DeviceDetailsModal
        open={isDeviceDetailsModalOpen}
        onClose={() => setIsDeviceDetailsModalOpen(false)}
        deviceDetails={parsedDeviceDetails}
      />

      {/* Modals */}
      <AnnotationEditor
        open={isAnnotationEditorOpen}
        onClose={() => setIsAnnotationEditorOpen(false)}
        imageUrl={
          view
            ? imageUrls.find(
                (_, index) =>
                  screenshotMap["full-page-screenshot.png"]?.index === index,
              ) || imageUrls[0]
            : imageUrls.find(
                (_, index) =>
                  screenshotMap["element-screenshot.png"]?.index === index,
              ) ||
              imageUrls[0] ||
              ""
        }
        onSave={handleSaveAnnotation}
        onInsertText={(text) =>
          setBugDetail((prev) => ({
            ...prev,
            description: (prev.description || "") + "\n" + text,
          }))
        }
      />

      <AnnotationEditor
        open={isAnnotatingAttachment}
        onClose={() => {
          setIsAnnotatingAttachment(false);
          setCurrentAttachmentIndex(null);
        }}
        imageUrl={
          currentAttachmentIndex !== null &&
          bugDetail.attachments[currentAttachmentIndex]
            ? bugDetail.attachments[currentAttachmentIndex].tempBase64 ||
              bugDetail.attachments[currentAttachmentIndex].url ||
              ""
            : ""
        }
        onSave={handleSaveAttachmentAnnotation}
        onInsertText={(text) =>
          setBugDetail((prev) => ({
            ...prev,
            description: (prev.description || "") + "\n" + text,
          }))
        }
      />

      <AttachmentViewModal
        open={isAttachmentViewOpen}
        onClose={() => setIsAttachmentViewOpen(false)}
        attachment={selectedAttachment}
      />

      {isDeleteReply && (
        <DeleteConfirmModal
          open={isDeleteReply}
          onClose={() => setIsDeleteReply(false)}
          onConfirm={handleDelete}
          confirmMessage={`${t("Are you sure you want to delete this")} ${
            bugDetailData?.type || t("task")
          }?`}
          loading={isDeleteLoading}
          title={t("Delete Task")}
          okText={t("Ok")}
        />
      )}

      {/* <TaskDependencies
        open={Boolean(dependenciesAnchorEl)}
        anchorEl={dependenciesAnchorEl}
        onClose={() => setDependenciesAnchorEl(null)}
        dependencies={dependencies}
        onUpdateDependencies={setDependencies}
        projectId={getProjectIdForDetails()}
      /> */}
      <Relationship
        open={Boolean(dependenciesAnchorEl)}
        onClose={() => setDependenciesAnchorEl(null)}
        dependencies={dependencies}
        onUpdateDependencies={setDependencies}
        projectId={getProjectIdForDetails()}
        projectList={projectlist || []}
        relatedBugs={bugDetailData?.relatedBugs || []}
        isTaskUpdate={true}
        currentTaskId={bugId}
      />
      <RecurringTaskPopover
        open={isRecurringModalOpen}
        anchorEl={recurringAnchorEl}
        onClose={handleRecurringModalClose}
        onSave={handleRecurringConfigSave}
        initialConfig={recurringConfig}
        availableStatus={availableStatus}
      />

      {/* Video Preview Panel Modal */}
      <Dialog
        open={isVideoPreviewOpen}
        onClose={() => setIsVideoPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        <VideoPreviewPanel
          videoUrl={previewVideoUrl || ""}
          networkLogs={bugDetail?.logs?.network || []}
          consoleLogs={bugDetail?.logs?.console || []}
          actionLogs={bugDetail?.logs?.actions || []}
          onClose={() => setIsVideoPreviewOpen(false)}
          isOpen={isVideoPreviewOpen}
          bugId={bugId}
          users={users}
          bugDetailData={bugDetailData}
          systemInfo={systemInfo}
        />
      </Dialog>

      <AiNotConfiguredDialog
        open={showAiConfigDialog}
        onClose={() => setShowAiConfigDialog(false)}
      />
    </>
  );
}

export default TaskUpdateDrawer;
